import { isPassedStatus, isSkippedStatus, isFailedStatus, computeSuccessRate, SKIPPED_OUTCOMES } from '../utils/testStatus';

class AzureDevOpsService {
  // Récupérer tous les bugs avec le champ TA à true
  async getBugsWithTA() {
    const wiql = `SELECT [System.Id], [System.Title], [System.State], [System.Tags], [System.AreaPath], [System.CreatedDate]
FROM workitems
WHERE [System.WorkItemType] = 'Bug'
  AND [TA] = 'True'
ORDER BY [System.CreatedDate] DESC`;

    try {
      // 1) Exécuter la requête WIQL
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      if (ids.length === 0) return [];

      // 2) Récupérer les work items en détails (API accepte jusqu'à 200 ids)
      const chunkSize = 200;
      const fields = [
        'System.Id',
        'System.Title',
        'System.State',
        'System.Tags',
        'System.AssignedTo',
        'System.CreatedBy',
        'System.CreatedDate',
        'System.ChangedDate',
        'System.AreaPath',
        'Microsoft.VSTS.Common.Severity',
        'RegressionField'
      ];

      const allWorkItems = [];
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const idsParam = chunk.join(',');
        const endpoint = `/wit/workitems?ids=${idsParam}&fields=${fields.join(',')}&api-version=7.0`;
        const res = await this.makeApiCall(endpoint);
        if (res && res.value) {
          allWorkItems.push(...res.value);
        }
      }

      const mapped = allWorkItems.map(w => {
        // Log pour debug - à retirer plus tard
        if (w.fields['RegressionField']) {
          console.log('Bug TA avec RegressionField:', w.id, w.fields['RegressionField']);
        }
        
        return {
          id: w.id,
          title: w.fields['System.Title'],
          state: w.fields['System.State'],
          tags: w.fields['System.Tags'] || '',
          areaPath: w.fields['System.AreaPath'] || '',
          assignedTo: w.fields['System.AssignedTo'] ? w.fields['System.AssignedTo'].displayName : null,
          createdBy: w.fields['System.CreatedBy'] ? w.fields['System.CreatedBy'].displayName : null,
          creationDate: w.fields['System.CreatedDate'],
          changedDate: w.fields['System.ChangedDate'],
          severity: w.fields['Microsoft.VSTS.Common.Severity'] || null,
          regression: w.fields['RegressionField'] === 'True' || w.fields['RegressionField'] === true,
          url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${w.id}`
        };
      });
      
      const withRegression = mapped.filter(b => b.regression).length;
      console.log(`Bugs TA total: ${mapped.length}, avec regression: ${withRegression}`);
      
      return mapped;
    } catch (error) {
      console.error('Erreur récupération bugs TA:', error);
      return [];
    }
  }

  // Récupérer tous les bugs avec le champ Regression à true
  async getBugsWithRegression() {
    const wiql = `SELECT [System.Id], [System.Title], [System.State], [System.Tags], [System.AreaPath], [System.CreatedDate]
FROM workitems
WHERE [System.WorkItemType] = 'Bug'
  AND [RegressionField] = 'True'
ORDER BY [System.CreatedDate] DESC`;

    try {
      // 1) Exécuter la requête WIQL
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      if (ids.length === 0) return [];

      // 2) Récupérer les work items en détails (API accepte jusqu'à 200 ids)
      const chunkSize = 200;
      const fields = [
        'System.Id',
        'System.Title',
        'System.State',
        'System.Tags',
        'System.AssignedTo',
        'System.CreatedBy',
        'System.CreatedDate',
        'System.ChangedDate',
        'System.AreaPath',
        'Microsoft.VSTS.Common.Severity',
        'TA'
      ];

      const allWorkItems = [];
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const idsParam = chunk.join(',');
        const endpoint = `/wit/workitems?ids=${idsParam}&fields=${fields.join(',')}&api-version=7.0`;
        const res = await this.makeApiCall(endpoint);
        if (res && res.value) {
          allWorkItems.push(...res.value);
        }
      }

      return allWorkItems.map(w => ({
        id: w.id,
        title: w.fields['System.Title'],
        state: w.fields['System.State'],
        tags: w.fields['System.Tags'] || '',
        areaPath: w.fields['System.AreaPath'] || '',
        assignedTo: w.fields['System.AssignedTo'] ? w.fields['System.AssignedTo'].displayName : null,
        createdBy: w.fields['System.CreatedBy'] ? w.fields['System.CreatedBy'].displayName : null,
        creationDate: w.fields['System.CreatedDate'],
        changedDate: w.fields['System.ChangedDate'],
        severity: w.fields['Microsoft.VSTS.Common.Severity'] || null,
        isTA: w.fields['TA'] === 'True' || w.fields['TA'] === true,
        url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${w.id}`
      }));
    } catch (error) {
      console.error('Erreur récupération bugs Regression:', error);
      return [];
    }
  }
  // Récupérer tous les tests ReadyApi
  async getAllReadyApiTests() {
    // À adapter selon la structure réelle des tests ReadyApi
    // Exemple : récupérer tous les test cases d'un certain type ou dossier
    // Ici, on suppose qu'il existe un endpoint ou une méthode pour cela
    // Remplacer par la vraie logique si besoin
    const endpoint = '/test/testcases?api-version=7.1-preview.1';
    const response = await this.makeApiCall(endpoint);
    // Filtrer par type ReadyApi si nécessaire
    return (response.value || []).filter(tc => tc.automationType === 'ReadyApi');
  }

  // Récupérer tous les tests RobotFramework
  async getAllRobotFrameworkTests() {
    // À adapter selon la structure réelle des tests RobotFramework
    const endpoint = '/test/testcases?api-version=7.1-preview.1';
    const response = await this.makeApiCall(endpoint);
    // Filtrer par type RobotFramework si nécessaire
    return (response.value || []).filter(tc => tc.automationType === 'RobotFramework');
  }
  constructor() {
    // Configuration - À modifier selon votre environnement
    this.organization = process.env.REACT_APP_AZURE_ORG || 'chapsvision';
    this.project = process.env.REACT_APP_AZURE_PROJECT || 'Fabrique';
    // Prefer env var, otherwise try to read from localStorage (set by UI)
    this.pat = process.env.REACT_APP_AZURE_PAT || (typeof window !== 'undefined' ? localStorage.getItem('AZDO_PAT') : null) || null;
    this.baseUrl = `https://dev.azure.com/${this.organization}/${this.project}/_apis`;
    
    // Headers d'authentification
    this.headers = {
      'Content-Type': 'application/json',
    };

    if (this.pat) {
      try {
        this.headers['Authorization'] = `Basic ${btoa(':' + this.pat)}`;
      } catch (e) {
        // btoa may fail in some environments; leave header unset and rely on setPat
        console.warn('Impossible d\'encoder le PAT pour Authorization header', e);
      }
    }

    // Debug flag (to reduce console spam in production)
    this.debug = String(process.env.REACT_APP_AZDO_DEBUG || '').toLowerCase() === 'true';

    // Import cache dynamically to avoid circular dependencies
    if (typeof window !== 'undefined') {
      import('./apiCache.js').then(module => {
        this.cache = module.default;
      }).catch(err => {
        console.warn('Cache non disponible:', err);
        this.cache = null;
      });
    } else {
      this.cache = null;
    }
  }
 

  // Récupérer les résultats de tests pour un build (optimisé: 1 appel par build)
  async getTestResultsForBuild(buildId) {
    // Stratégie par défaut: passer par les runs (compatible et évite le 405)
    try {
      const runsEndpoint = `/test/runs?buildUri=vstfs:///Build/Build/${buildId}&api-version=7.1`;
      const runs = await this.makeApiCall(runsEndpoint);
      const all = [];
      for (const run of (runs.value || [])) {
        try {
          const runResults = await this.fetchAllTestResults(run.id, { expectedTotal: run.totalTests ?? null });
          runResults.forEach(r => {
            // Injecter quelques champs utiles du run si manquants pour l'horodatage
            if (!r.completedDate && run.completedDate) r.completedDate = run.completedDate;
            if (!r.startedDate && run.startedDate) r.startedDate = run.startedDate;
          });
          all.push(...runResults);
        } catch (e) {
          console.warn('Erreur résultats pour run', run.id, e);
        }
      }
      return all;
    } catch (e2) {
      if (this.debug) {
        console.warn('getTestResultsForBuild via runs a échoué', e2);
      }
      // Tentative secondaire: endpoint direct (peut retourner 405 selon l'orga)
      try {
        const resultsEndpoint = `/test/results?buildId=${buildId}&$top=100000&api-version=7.1-preview.6`;
        const results = await this.makeApiCall(resultsEndpoint);
        return results.value || [];
      } catch (err) {
        console.error('Impossible de récupérer les résultats pour le build', buildId, err);
        return [];
      }
    }
  }

  // Méthode générique pour les appels API (avec cache)
  async makeApiCall(endpoint, options = {}) {
    try {
      if (!this.pat) {
        throw new Error('No Personal Access Token (PAT) configured. Please login with your Azure DevOps PAT.');
      }

      const url = `${this.baseUrl}${endpoint}`;
      
      // Check cache first (only for GET requests)
      const method = options.method || 'GET';
      if (this.cache && method === 'GET') {
        const cached = this.cache.get(url, options);
        if (cached !== null) {
          return cached;
        }
      }

      const response = await fetch(url, {
        headers: this.headers,
        ...options
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          this.handleUnauthorized();
        }
        let responseText = '';
        try {
          responseText = await response.text();
        } catch {
          responseText = '';
        }
        const message = responseText && responseText.trim()
          ? `HTTP error! status: ${response.status} - ${responseText}`
          : `HTTP error! status: ${response.status}`;
        throw new Error(message);
      }

      // Vérifier si la réponse a du contenu avant de parser le JSON
      const text = await response.text();
      if (!text || text.trim() === '') {
        console.warn('Empty response received from API');
        return {};
      }
      
      const data = JSON.parse(text);
      
      // Store in cache (only for successful GET requests)
      if (this.cache && method === 'GET' && response.ok) {
        this.cache.set(url, data, options);
      }

      return data;
    } catch (error) {
      console.error('API call failed:', error);
      throw error;
    }
  }

  // Variante qui renvoie aussi les headers (utile pour la pagination continuationToken)
  async makeApiCallWithHeaders(endpoint, options = {}) {
    if (!this.pat) {
      throw new Error('No Personal Access Token (PAT) configured. Please login with your Azure DevOps PAT.');
    }

    const url = `${this.baseUrl}${endpoint}`;
    const response = await fetch(url, {
      headers: this.headers,
      ...options,
    });

    if (!response.ok) {
      if (response.status === 401 || response.status === 403) {
        this.handleUnauthorized();
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const text = await response.text();
    const data = text && text.trim() !== '' ? JSON.parse(text) : {};

    const headers = {};
    response.headers.forEach((value, key) => {
      headers[key.toLowerCase()] = value;
    });

    return { data, headers };
  }

  // Set / get PAT at runtime (used by login UI)
  setPat(pat) {
    this.pat = pat;
    if (typeof window !== 'undefined') {
      try {
        localStorage.setItem('AZDO_PAT', pat);
      } catch (e) {
        console.warn('localStorage inaccessible :', e);
      }
    }
    try {
      this.headers['Authorization'] = `Basic ${btoa(':' + pat)}`;
    } catch (e) {
      console.warn('Impossible d\'encoder le PAT pour Authorization header', e);
    }
  }

  getPat() {
    return this.pat;
  }

  clearPat() {
    this.pat = null;
    if (typeof window !== 'undefined') {
      try { localStorage.removeItem('AZDO_PAT'); } catch (e) { /* ignore */ }
    }
    delete this.headers['Authorization'];
  }

  handleUnauthorized() {
    this.clearPat();
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('azdo-auth-invalid'));
      } catch (e) {
        console.warn('Impossible de dispatcher azdo-auth-invalid', e);
      }
    }
  }

  async validatePat(pat = this.pat) {
    if (!pat) {
      return { valid: false, message: 'PAT manquant' };
    }

    const validationUrl = `${this.baseUrl}/build/builds?$top=1&api-version=7.1`;
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Basic ${btoa(':' + pat)}`
    };

    try {
      const response = await fetch(validationUrl, { method: 'GET', headers });

      if (response.status === 200) {
        const body = await response.json().catch(() => ({}));
        if (body && Array.isArray(body.value)) {
          return { valid: true };
        }
      }

      if (response.status === 401 || response.status === 403 || response.status === 203) {
        return { valid: false, message: 'PAT invalide ou expiré' };
      }

      return { valid: false, message: `Validation impossible (HTTP ${response.status})` };
    } catch (error) {
      return { valid: false, message: 'Erreur réseau lors de la validation du PAT' };
    }
  }

  // === Méthodes Test Plan SUPPRIMÉES ===
  // Ces méthodes causaient des cascades d'appels API infinies
  // Utiliser getAutomatedTestHistory() à la place avec une liste prédéfinie de tests

  // Récupérer les pull requests d'un repository (pagination $skip/$top)
  async getPullRequestsByRepository(repositoryId, fromDate = null, status = 'completed') {
    const pageSize = 100; // limite API par page
    let skip = 0;
    let all = [];
    const minTs = fromDate ? new Date(fromDate).getTime() : null;
    // Filtrage côté serveur pour limiter le nombre de pages sur les grandes fenêtres.
    const minTimeParam = fromDate
      ? `&searchCriteria.minTime=${encodeURIComponent(new Date(fromDate).toISOString())}`
      : '';

    let hasMore = true;
    while (hasMore) {
      // Note : la pagination par header x-ms-continuationtoken ne fonctionne pas ici — les
      // appels sont cross-origin vers dev.azure.com et ce header n'est pas exposé via CORS
      // (Access-Control-Expose-Headers), donc il est toujours lu comme absent côté navigateur.
      // On utilise $skip/$top comme le fait déjà getUntaggedWorkItemsFromPRsOn261Branch.
      const endpoint = `/git/pullrequests?searchCriteria.repositoryId=${repositoryId}&searchCriteria.status=${status}${minTimeParam}&$top=${pageSize}&$skip=${skip}&api-version=7.1-preview.1`;

      try {
        const data = await this.makeApiCall(endpoint);
        const page = data.value || [];
        all.push(...page);

        hasMore = page.length === pageSize;
        skip += pageSize;
      } catch (e) {
        console.warn(`Erreur récupération PRs repo=${repositoryId} (status=${status})`, e);
        break;
      }
    }

    // Dédupliquer par pullRequestId
    const map = new Map();
    for (const pr of all) {
      if (pr && pr.pullRequestId != null) {
        map.set(pr.pullRequestId, pr);
      }
    }

    let result = Array.from(map.values());

    // Filtrer par date de création si demandé
    if (minTs !== null) {
      result = result.filter(pr => {
        const ts = pr.creationDate ? new Date(pr.creationDate).getTime() : 0;
        return ts >= minTs;
      });
    }

    return result;
  }

  // Récupérer les PRs d'un repository filtrées par auteurs et date
  async getPullRequestsByAuthors(repositoryId, authors = [], fromDate = null) {
    const allPrs = await this.getPullRequestsByRepository(repositoryId, fromDate);
    
    // Filtrer par auteurs
    if (authors.length === 0) {
      return allPrs;
    }
    
    const authorEmails = authors.map(a => a.toLowerCase());
    
    return allPrs.filter(pr => {
      const creatorEmail = pr.createdBy?.uniqueName?.toLowerCase() || '';
      return authorEmails.includes(creatorEmail);
    });
  }

  // Récupérer un repository par nom
  async getRepositoryByName(repoName) {
    const repos = await this.getRepositories();
    return repos.find(r => r.name === repoName);
  }

  // Récupérer les PRs fusionnées d'un repository avec les détails (créateur, dates, etc.)
  async getMergedPullRequestsWithDetails(repositoryId, authors = [], fromMonthsAgo = 6) {
    // Calculer la date de 6 mois en arrière
    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - fromMonthsAgo);
    
    // Récupérer les PRs
    const prs = await this.getPullRequestsByAuthors(repositoryId, authors, fromDate);
    
    // Filtrer seulement les PRs complétées (merged ou abandoned)
    const mergedPrs = prs.filter(pr => pr.status === 'completed' && pr.mergeStatus === 'succeeded');
    
    // Formater les données
    return mergedPrs.map(pr => ({
      id: pr.pullRequestId,
      title: pr.title,
      description: pr.description,
      status: pr.status,
      mergeStatus: pr.mergeStatus,
      createdBy: pr.createdBy?.displayName || 'Unknown',
      createdByEmail: pr.createdBy?.uniqueName || '',
      creationDate: pr.creationDate,
      closedDate: pr.closedDate,
      sourceRefName: pr.sourceRefName,
      targetRefName: pr.targetRefName,
      commits: pr.commits?.length || 0,
      reviewers: pr.reviewers?.length || 0,
      url: pr.url,
      repository: pr.repository?.name || ''
    }));
  }

  // Récupérer tous les repositories et en extraire l'ID par nom
  async getRepositoryIdByName(repoName) {
    const repo = await this.getRepositoryByName(repoName);
    return repo ? repo.id : null;
  }

  // Récupérer tous les repositories
  async getRepositories() {
    const endpoint = `/git/repositories?api-version=7.1`;
    const response = await this.makeApiCall(endpoint);
    return response.value || [];
  }

  // Récupérer tous les pipelines
  async getPipelines() {
    const endpoint = '/pipelines?api-version=7.0';
    const response = await this.makeApiCall(endpoint);
    return response.value || [];
  }

  // Récupérer le dernier build d'un pipeline
  async getLatestBuildForPipeline(pipelineId) {
    // On récupère les derniers builds (incluant potentiellement un build inProgress) puis on choisit celui qui est en cours si présent
    const url = `/build/builds?definitions=${pipelineId}&$top=5&queryOrder=queueTimeDescending&api-version=7.0`;
    const response = await this.makeApiCall(url);
    const builds = response.value || [];
    if (builds.length === 0) return null;
    const activeStatuses = ['inprogress', 'notstarted', 'postponed'];
    const active = builds.find(b => activeStatuses.includes((b.status || '').toLowerCase()));
    return active || builds[0];
  }

  // Récupérer les runs d'un build
  async getTestRunsByBuildId(buildId) {
    const url = `/test/runs?buildUri=vstfs:///Build/Build/${buildId}&api-version=7.1`;
    const response = await this.makeApiCall(url);
    return response.value || [];
  }

  // Récupérer les runs d'un pipeline
  async getPipelineRuns(pipelineId, top = 10) {
    const endpoint = `/pipelines/${pipelineId}/runs?api-version=7.0&$top=${top}`;
    const response = await this.makeApiCall(endpoint);
    return response.value || [];
  }

  // Récupérer un build simple (sans timeline) pour extraire la branche, etc.
  async getBuild(buildId) {
    try {
      const build = await this.makeApiCall(`/build/builds/${buildId}?api-version=7.0`);
      return build;
    } catch (error) {
      console.error(`Erreur récupération build ${buildId}:`, error);
      return null;
    }
  }

  // Récupérer l'environnement de test dynamiquement depuis les templateParameters du build
  async getEnvironmentFromBuild(buildId) {
    try {
      const numericId = String(buildId).replace('build-', '');
      const build = await this.makeApiCall(`/build/builds/${numericId}?api-version=7.0`);
      if (!build) return null;

      // YAML pipelines : templateParameters via /pipelines/{pipelineId}/runs/{buildId}
      const pipelineId = build.definition?.id;
      if (pipelineId) {
        try {
          const run = await this.makeApiCall(`/pipelines/${pipelineId}/runs/${numericId}?api-version=7.1-preview.1`);
          const env = run?.templateParameters?.envadm
            || run?.templateParameters?.environment
            || run?.templateParameters?.env;
          if (env) return env;
        } catch {}
      }

      // Fallback : champ parameters (JSON string) du build
      if (build.parameters) {
        try {
          const params = JSON.parse(build.parameters);
          const env = params.envadm || params.environment || params.env;
          if (env) return env;
        } catch {}
      }

      return null;
    } catch {
      return null;
    }
  }

  // Récupérer un pipeline run pour extraire les templateParameters (paramètres runtime)
  async getPipelineRun(pipelineId, runId) {
    try {
      const run = await this.makeApiCall(`/pipelines/${pipelineId}/runs/${runId}?api-version=7.1-preview.1`);
      return run;
    } catch (error) {
      console.error(`Erreur récupération pipeline run ${pipelineId}/${runId}:`, error);
      return null;
    }
  }

  // Récupérer les détails d'un run spécifique
  async getRunDetails(pipelineId, runId) {
    const endpoint = `/pipelines/${pipelineId}/runs/${runId}?api-version=7.0`;
    return await this.makeApiCall(endpoint);
  }

  // Récupérer les résultats de tests pour un run
  async getTestResults(runId) {
    const endpoint = `/test/runs?buildUri=vstfs:///Build/Build/${runId}&api-version=7.0`;
    const response = await this.makeApiCall(endpoint);
    
    if (response.value && response.value.length > 0) {
      const testRunId = response.value[0].id;
      return await this.getTestRunResults(testRunId);
    }
    return [];
  }

  // Récupérer les résultats détaillés d'un test run
  async getTestRunResults(testRunId) {
    return await this.fetchAllTestResults(String(testRunId).replace('run-', ''));
  }

  // Mettre à jour l'outcome d'un résultat de test dans un run (non reproduit → Passed)
  async updateTestResultOutcome(runId, testResultId, comment = 'Non reproduit') {
    const numericRunId = String(runId).replace('run-', '');
    return await this.makeApiCall(`/test/runs/${numericRunId}/results?api-version=7.0`, {
      method: 'PATCH',
      body: JSON.stringify([{
        id: Number(testResultId),
        outcome: 'Passed',
        comment
      }])
    });
  }

  async getTestCaseDetails(testCaseId) {
    const endpoint = `/test/testcases/${testCaseId}?api-version=7.1-preview.1`;
    const response = await this.makeApiCall(endpoint);
    return response.value || [];
  }

  // Trouver l'ID du work item "Test Case" à partir du titre exact
  async getTestCaseWorkItemIdByTitle(title) {
    if (!title) return null;
    // WIQL n'aime pas les quotes simples non-échappées
    const safeTitle = String(title).replace(/'/g, "''");
    const wiql = `SELECT [System.Id] FROM workitems WHERE [System.WorkItemType] = 'Test Case' AND [System.Title] = '${safeTitle}'`;

    try {
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      return ids.length > 0 ? ids[0] : null;
    } catch (err) {
      console.warn('Erreur getTestCaseWorkItemIdByTitle:', err, title);
      return null;
    }
  }

  // Cherche des test cases par nom (WIQL CONTAINS) — utilisé par la barre de recherche globale
  async searchTestCases(query) {
    if (!query || query.trim().length < 2) return [];
    const escaped = query.trim().replace(/'/g, "''");
    const wiql = `SELECT [System.Id] FROM workitems WHERE [System.WorkItemType] = 'Test Case' AND [System.Title] CONTAINS '${escaped}' ORDER BY [System.ChangedDate] DESC`;
    try {
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });
      const refs = (wiqlResponse.workItems || []).slice(0, 20);
      if (refs.length === 0) return [];
      const ids = refs.map(wi => wi.id).join(',');
      const detailResponse = await this.makeApiCall(`/wit/workitems?ids=${ids}&$select=System.Title&api-version=7.0`);
      return (detailResponse.value || []).map(wi => ({
        id: wi.id,
        title: wi.fields?.['System.Title'] || `Test Case #${wi.id}`
      }));
    } catch (err) {
      console.warn('Erreur searchTestCases:', err);
      return [];
    }
  }

  // Trouve le pipeline/build/run/suite le plus récent qui contient ce test case
  // Filtre côté client sur testCaseTitle (le paramètre testCaseId de l'API AzDO n'est pas supporté comme filtre)
  async findTestCaseLocation(testCaseWorkItemId, testCaseTitle, pipelineIds) {
    const normalTitle = (testCaseTitle || '').trim().toLowerCase();

    for (const pipelineId of pipelineIds) {
      try {
        const buildsResponse = await this.makeApiCall(`/build/builds?api-version=7.0&definitions=${pipelineId}&$top=2&statusFilter=completed`);
        const builds = buildsResponse.value || [];

        for (const build of builds) {
          const runsResponse = await this.makeApiCall(`/test/runs?api-version=7.0&buildUri=${encodeURIComponent(build.uri)}`);
          const runs = runsResponse.value || [];

          for (const run of runs) {
            try {
              const resultsResponse = await this.makeApiCall(`/test/runs/${run.id}/results?api-version=7.0&$top=500`);
              const results = resultsResponse.value || [];

              const match = results.find(r => {
                const byId = r.testCase?.id != null && String(r.testCase.id) === String(testCaseWorkItemId);
                const byTitle = normalTitle && (r.testCaseTitle || '').trim().toLowerCase() === normalTitle;
                return byId || byTitle;
              });

              if (match) {
                return { pipelineId, buildId: build.id, runId: run.id, suiteName: run.name };
              }
            } catch { /* run inaccessible, on continue */ }
          }
        }
      } catch (err) {
        console.warn(`findTestCaseLocation: erreur pipeline ${pipelineId}`, err);
      }
    }
    return null;
  }

  // Récupérer les bugs liés (Related Work) à un test case (par titre de test case)
  // Retourne les work items de type Bug dont l'état contient 'new' ou 'dev' (case-insensitive)
  async getRelatedBugsForTestCaseByTitle(testCaseTitle) {
    try {
      const testCaseId = await this.getTestCaseWorkItemIdByTitle(testCaseTitle);
      if (!testCaseId) return [];

      // Récupérer le work item avec les relations
      const wi = await this.makeApiCall(`/wit/workitems/${testCaseId}?api-version=7.0&$expand=relations`);
      const relations = wi.relations || [];

      // Extraire les ids cibles des relations
      const relatedIds = new Set();
      const idRe = /\/workItems\/(\d+)$/i;
      for (const rel of relations) {
        if (!rel || !rel.url) continue;
        const m = String(rel.url).match(idRe);
        if (m && m[1]) relatedIds.add(Number(m[1]));
      }

      if (relatedIds.size === 0) return [];

      const idsArr = Array.from(relatedIds);
      // Récupérer en batch les work items trouvés
      const chunkSize = 200;
      const fields = ['System.Id', 'System.WorkItemType', 'System.Title', 'System.State'];
      const all = [];

      for (let i = 0; i < idsArr.length; i += chunkSize) {
        const chunk = idsArr.slice(i, i + chunkSize).join(',');
        const endpoint = `/wit/workitems?ids=${chunk}&fields=${fields.join(',')}&api-version=7.0`;
        const res = await this.makeApiCall(endpoint);
        if (res && res.value) all.push(...res.value);
      }

      // Filtrer uniquement les bugs et états 'new' ou 'dev' ou 'to be refined'
      const filtered = (all || []).filter(w => {
        const type = (w.fields['System.WorkItemType'] || '').toString().toLowerCase();
        const state = (w.fields['System.State'] || '').toString().toLowerCase();
        if (type !== 'bug') return false;
        return state.includes('new') || state.includes('dev')|| state.includes('to be refined')|| state.includes('test');
      }).map(w => ({
        id: w.id,
        title: w.fields['System.Title'],
        state: w.fields['System.State'],
        url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${w.id}`
      }));

      return filtered;
    } catch (error) {
      console.error('Erreur getRelatedBugsForTestCaseByTitle:', error, testCaseTitle);
      return [];
    }
  }

  // Récupérer un résumé léger d'un work item (pour valider un ID de bug avant liaison)
  async getWorkItemSummary(workItemId) {
    const id = Number(workItemId);
    if (!Number.isFinite(id)) {
      throw new Error(`ID de work item invalide: ${workItemId}`);
    }

    const wi = await this.makeApiCall(
      `/wit/workitems/${id}?fields=System.Title,System.WorkItemType,System.State&api-version=7.0`
    );

    return {
      id,
      title: wi.fields?.['System.Title'] || `#${id}`,
      type: wi.fields?.['System.WorkItemType'] || '',
      state: wi.fields?.['System.State'] || '',
      url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${id}`
    };
  }

  // Lier un bug existant à un Test Case (relation "Related" côté ADO)
  async linkBugToTestCase(testCaseId, bugId) {
    const tcId = Number(testCaseId);
    const bId = Number(bugId);
    if (!Number.isFinite(tcId)) {
      throw new Error(`Test Case id invalide: ${testCaseId}`);
    }
    if (!Number.isFinite(bId)) {
      throw new Error(`Bug id invalide: ${bugId}`);
    }

    const patchBody = [
      {
        op: 'add',
        path: '/relations/-',
        value: {
          rel: 'System.LinkTypes.Related',
          url: `${this.baseUrl}/wit/workItems/${bId}`
        }
      }
    ];

    const url = `${this.baseUrl}/wit/workitems/${tcId}?api-version=7.0`;
    const response = await fetch(url, {
      method: 'PATCH',
      headers: {
        ...this.headers,
        'Content-Type': 'application/json-patch+json'
      },
      body: JSON.stringify(patchBody)
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(
        text && text.trim()
          ? `HTTP ${response.status} - ${text}`
          : `HTTP ${response.status}`
      );
    }

    return { testCaseId: tcId, bugId: bId };
  }

  // Lier un bug existant à un Test Case identifié par le titre du test
  async linkBugToTestCaseByTitle(testCaseTitle, bugId) {
    const testCaseId = await this.getTestCaseWorkItemIdByTitle(testCaseTitle);
    if (!testCaseId) {
      throw new Error(`Test Case introuvable pour "${testCaseTitle}"`);
    }
    return this.linkBugToTestCase(testCaseId, bugId);
  }

  async getBuildsData(pipelines) {

    const allData = [];

    for (const pipeline of pipelines) {
        try {
            const build = await this.getLatestBuildForPipeline(pipeline.id);
            if (!build) continue;

            const runs = await this.getTestRunsByBuildId(build.id);
            if (!runs.length) {
              const buildStatus = (build.status || '').toLowerCase();
              if (['inprogress', 'notstarted', 'postponed'].includes(buildStatus)) {
                allData.push({
                  id: pipeline.id,
                  name: pipeline.name,
                  lastBuildId: build.id,
                  status: buildStatus,
                  isRunning: true,
                  hasNoResults: true,
                  executionDate: build.queueTime || null,
                  testEnvironment: 'En cours...',
                  totalDuration: 0,
                  passedTests: 0,
                  failedTests: 0,
                  skippedTests: 0,
                  totalTests: 0,
                  successRate: '0.0'
                });
              }
              continue;
            }

            let totalTests = 0, passedTests = 0, failedTests = 0, skippedTests = 0, totalDuration = 0;

            for (const run of runs) {
                const results = await this.getTestRunResults(run.id);

                for (const test of results) {
                    totalTests++;
                    // isFailedStatus est un catch-all (Inconclusive, Aborted, None…) : garantit
                    // l'invariant total = passés + échecs + ignorés, comme le fait déjà
                    // getTestSuitesForBuild pour Robot Framework.
                    if (isPassedStatus(test.outcome)) passedTests++;
                    else if (isSkippedStatus(test.outcome)) skippedTests++;
                    else if (isFailedStatus(test.outcome)) failedTests++;

                    totalDuration += test.durationInMs || 0;
                }
            }

            // Environnement récupéré dynamiquement depuis les templateParameters du build
            const testEnvironment = await this.getEnvironmentFromBuild(build.id);

            allData.push({
                id: pipeline.id,
                name: pipeline.name,
                lastBuildId: build.id,
                status: build.result?.toLowerCase() || 'unknown',
                isRunning: ['inprogress', 'notstarted', 'postponed'].includes((build.status || '').toLowerCase()),
                executionDate: build.finishTime || build.queueTime || null,
                testEnvironment: testEnvironment || 'Non défini',
                totalDuration: Math.round(totalDuration / 1000), // seconds
                passedTests,
                failedTests,
                skippedTests,
                totalTests,
                successRate: computeSuccessRate(passedTests, totalTests, skippedTests).toFixed(2)
            });

        } catch (err) {
            console.error(`❌ Erreur pipeline ${pipeline.name}`, err);
        }
    }

    return allData;
  }


  // Récupérer l'historique des tests pour un test case spécifique
  async getAutomatedTestHistory(automatedTestName, buildDefinitionId) {
    try {
      // Récupérer les test runs récents
      const buildEndpoint = `/build/builds?definitions=${buildDefinitionId}&$top=10&queryOrder=finishTimeDescending&api-version=7.0`;
      const buildData = await this.makeApiCall(buildEndpoint);
      
      const historyResults = [];
      
      for (const build of buildData.value || []) {
        try {
          const runsEndpoint = `/test/runs?buildUri=vstfs:///Build/Build/${build.id}&$top=100&api-version=7.1`;
          const runs = await this.makeApiCall(runsEndpoint);
          
          if (!runs.value || runs.value.length === 0) {
            continue;
          }
          for (const run of runs.value) {
            const resultsEndpoint = `/test/runs/${run.id}/results?api-version=7.1-preview.6`;
            const results = await this.makeApiCall(resultsEndpoint);
            
            const matchingResults = results.value?.filter(result => {
              const atn = result.automatedTestName || '';
              const tct = result.testCaseTitle || '';
              return atn === automatedTestName ||
                atn.slice(atn.lastIndexOf('.') + 1) === automatedTestName ||
                tct === automatedTestName;
            }) || [];
            
            for (const result of matchingResults) {
              // Debug dates (activable via REACT_APP_AZDO_DEBUG)
              if (this.debug) {
                console.log('Dates disponibles:', {
                  runCompleted: run.completedDate,
                  runStarted: run.startedDate,
                  buildCompleted: run.build?.finishTime,
                  buildStarted: run.build?.startTime,
                  resultCompleted: result.completedDate,
                  resultStarted: result.startedDate
                });
              }
              
              // Essayer plusieurs sources de date
              const dateSource = run.completedDate || 
                                run.startedDate || 
                                run.build?.finishTime || 
                                run.build?.startTime ||
                                result.completedDate ||
                                result.startedDate;
              
              let formattedDate = null;
              let timestamp = Date.now();
              
              if (dateSource) {
                try {
                  const dateObj = new Date(dateSource);
                  if (!isNaN(dateObj.getTime())) {
                    formattedDate = dateObj.toISOString().split('T')[0];
                    timestamp = dateObj.getTime();
                  }
                } catch (e) {
                  console.warn('Erreur parsing date:', dateSource, e);
                }
              }
              
              // Si toujours pas de date, utiliser l'index comme fallback
              if (!formattedDate) {
                const fallbackDate = new Date(Date.now() - (historyResults.length * 24 * 60 * 60 * 1000));
                formattedDate = fallbackDate.toISOString().split('T')[0];
                timestamp = fallbackDate.getTime();
              }
              
              historyResults.push({
                date: formattedDate,
                dateTimestamp: timestamp,
                status: result.outcome?.toLowerCase() || 'unknown',
                duration: Math.round((result.durationInMs || 0) / 1000),
                durationSeconds: Math.round((result.durationInMs || 0) / 1000),
                buildId: build.id,
                buildNumber: build.buildNumber || run.build?.buildNumber || `Build-${build.id}`,
                logUrl: `https://dev.azure.com/${this.organization}/${this.project}/_TestManagement/Runs?runId=${run.id}&_a=resultSummary&resultId=${result.id}`,
                testRunId: run.id
              });
            }

          }
        } catch (error) {
            console.warn(`Erreur pour le test run :`, error);
          }          
      }
      
      return historyResults
        .sort((a, b) => new Date(b.date) - new Date(a.date))
        .slice(0, 20);
        
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'historique:', error);
      return [];
    }
  }



  // Récupérer les statistiques des tests (dernières 24h)
  async getTestStatistics(hours = 24) {
    const toDate = new Date();
    const fromDate = new Date();
    fromDate.setHours(toDate.getHours() - hours);
    
    const endpoint = `/test/runs?api-version=7.0&minLastUpdatedDate=${fromDate.toISOString()}`;
    const response = await this.makeApiCall(endpoint);
    
    let totalTests = 0;
    let passedTests = 0;
    let failedTests = 0;
    
    for (const testRun of response.value || []) {
      totalTests += testRun.totalTests || 0;
      passedTests += testRun.passedTests || 0;
      failedTests += testRun.unanalyzedTests || 0; // Les tests échoués
    }
    
    return {
      total: totalTests,
      passed: passedTests,
      failed: failedTests,
      successRate: totalTests > 0 ? ((passedTests / totalTests) * 100).toFixed(1) : 0
    };
  }

  // Transformer les données API en format pour le dashboard
  async getDashboardData() {
      const specificPipelineIds = [
            { id: 304, name: "argonos-tests-dp" },
            { id: 305, name: "argonos-tests-de" },
            { id: 303, name: "argonos-tests-admin" },
            { id: 355, name: "argonos-tests-Investigation" }
        ];
      const dashboardData = [];
      
      for (const pipeline of specificPipelineIds) {
        try {
            // 🔹 Étape 1 : récupérer le dernier build pour ce pipeline
            const latestBuild = await this.getLatestBuildForPipeline(pipeline.id);
            if (!latestBuild) {
                console.warn(`⚠️ Aucun build trouvé pour ${pipeline.name}`);
                continue;
            }

            // 🔹 Étape 2 : récupérer tous les test runs liés à ce build
            const testRuns = await this.getTestRunsByBuildId(latestBuild.id);
            if (!testRuns || testRuns.length === 0) {
                console.warn(`⚠️ Aucun test run trouvé pour le build ${latestBuild.id} (${pipeline.name})`);
                continue;
            }

            for (const run of testRuns) {
                const testResults = await this.getTestRunResults(run.id);
                for (const test of testResults || []) {

                    dashboardData.push({
                        id: test.id,
                        name: pipeline.name,
                        testSuite: test.testRun?.name || 'N/A',
                        testCase: test.automatedTestName || 'N/A',
                        status: this.mapTestOutcome(test.outcome),
                        failureDuration: this.calculateFailureDuration(test),
                        executionDate: run.createdDate,
                        duration: test.durationInMs ? Math.round(test.durationInMs / 1000) : 0,
                        errorMessage: test.errorMessage || 'N/A',
                        stackTrace: test.stackTrace || 'N/A',
                        buildNumber: run.name
                  });
                }
            }
        } catch (error) {
            console.error(`Erreur lors du traitement du pipeline ${pipeline.name}:`, error);
            }
        }
        return dashboardData;
    }

  // Mapper les résultats Azure DevOps vers notre format
  mapTestOutcome(outcome) {
    const outcomeMap = {
      'Passed': 'success',
      'Failed': 'failed',
      'Skipped': 'skipped',
      'NotExecuted': 'not_executed',
      'Inconclusive': 'inconclusive'
    };
    return outcomeMap[outcome] || 'unknown';
  }

  // Calculer la durée d'échec (simplifié - nécessite une logique plus complexe)
  calculateFailureDuration(testResult) {
    if (testResult.outcome !== 'Failed') return null;
    
    // Ici vous devriez implémenter une logique pour calculer
    // depuis combien de temps le test est en échec
    // Cela nécessite de comparer avec les exécutions précédentes
    
    const completedDate = new Date(testResult.completedDate);
    const now = new Date();
    const diffTime = Math.abs(now - completedDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  }

  // Récuperer les derniers build de chaque pipeline
  async getRecentBuildsByPipeline(pipelineId, top = 5) {
    const buildsByPipeline = {};

    const endpoint = `/build/builds?definitions=${pipelineId}&$top=${top}&queryOrder=finishTimeDescending&api-version=7.0`;

    try {
      const response = await this.makeApiCall(endpoint);

      const builds = await Promise.all(response.value.map(async (b) => {
        const start = new Date(b.startTime);
        const finish = new Date(b.finishTime);
        const duration = ((finish - start) / 1000); // en secondes

        const testRunsRes = await this.makeApiCall(`/test/runs?buildUri=vstfs:///Build/Build/${b.id}&api-version=7.1`);
        // notApplicableTests = tests non exécutés (SKIP Robot Framework) : ni passés ni échoués
        let passed = 0, total = 0, skipped = 0;
        for (const run of testRunsRes.value || []) {
          total += run.totalTests || 0;
          passed += run.passedTests || 0;
          skipped += run.notApplicableTests || 0;
        }

        return {
          buildId: `build-${b.id}`,
          status: b.result,
          date: b.finishTime,
          duration: duration,
          totalTests: total,
          passedTests: passed,
          failedTests: total - passed - skipped,
          skippedTests: skipped,
        };
      }));
      buildsByPipeline[`pipeline-${pipelineId}`] = builds;
    } catch (error) {
      console.error(`❌ Erreur lors de la récupération des builds pour pipeline ${pipelineId}`, error);
      buildsByPipeline[`pipeline-${pipelineId}`] = [];
    }
    
    return buildsByPipeline;
  }

  // Fetch the latest build for a pipeline within a given session window.
  // sessionKey: YYYY-MM-DD (local date) — session covers sessionKey@20:00 → (sessionKey+1)@19:59:59 local time.
  async getBuildsBySessionKey(pipelineId, sessionKey) {
    const [year, month, day] = sessionKey.split('-').map(Number);
    const minTime = new Date(year, month - 1, day, 20, 0, 0, 0);
    const maxTime = new Date(year, month - 1, day + 1, 19, 59, 59, 999);
    const endpoint = `/build/builds?api-version=7.0&definitions=${pipelineId}&minTime=${minTime.toISOString()}&maxTime=${maxTime.toISOString()}&queryOrder=finishTimeDescending&$top=10`;
    try {
      const response = await this.makeApiCall(endpoint);
      return (response.value || [])
        .filter(b => b.finishTime)
        .map(b => ({
          buildId: `build-${b.id}`,
          status: b.result,
          date: b.finishTime,
          duration: (new Date(b.finishTime) - new Date(b.startTime)) / 1000,
        }));
    } catch (error) {
      console.error(`Error fetching builds for session ${sessionKey}, pipeline ${pipelineId}:`, error);
      return [];
    }
  }

  // Same as getBuildsData but restricted to a session window (for date-click feature).
  async getBuildsDataForSession(pipelines, sessionKey) {
    const [year, month, day] = sessionKey.split('-').map(Number);
    const minTime = new Date(year, month - 1, day, 20, 0, 0, 0);
    const maxTime = new Date(year, month - 1, day + 1, 19, 59, 59, 999);
    const allData = [];

    for (const pipeline of pipelines) {
      try {
        const endpoint = `/build/builds?api-version=7.0&definitions=${pipeline.id}&minTime=${minTime.toISOString()}&maxTime=${maxTime.toISOString()}&queryOrder=finishTimeDescending&$top=5`;
        const response = await this.makeApiCall(endpoint);
        const builds = (response.value || []).filter(b => b.finishTime);
        if (!builds.length) continue;
        const build = builds[0];

        const runs = await this.getTestRunsByBuildId(build.id);
        if (!runs.length) continue;

        let totalTests = 0, passedTests = 0, failedTests = 0, skippedTests = 0, totalDuration = 0;
        for (const run of runs) {
          const results = await this.getTestRunResults(run.id);
          for (const test of results) {
            totalTests++;
            // Même convention que getBuildsData : les échecs sont le catch-all.
            if (isPassedStatus(test.outcome)) passedTests++;
            else if (isSkippedStatus(test.outcome)) skippedTests++;
            else if (isFailedStatus(test.outcome)) failedTests++;
            totalDuration += test.durationInMs || 0;
          }
        }

        const testEnvironment = await this.getEnvironmentFromBuild(build.id);
        allData.push({
          id: pipeline.id,
          name: pipeline.name,
          lastBuildId: build.id,
          status: build.result?.toLowerCase() || 'unknown',
          isRunning: false,
          executionDate: build.finishTime || null,
          testEnvironment: testEnvironment || 'Non défini',
          totalDuration: Math.round(totalDuration / 1000),
          passedTests,
          failedTests,
          skippedTests,
          totalTests,
          successRate: computeSuccessRate(passedTests, totalTests, skippedTests).toFixed(2)
        });
      } catch (err) {
        console.error(`Error getBuildsDataForSession pipeline ${pipeline.name}:`, err);
      }
    }
    return allData;
  }

  async hasRunningBuild(pipelineId) {
    try {
      const url = `/build/builds?definitions=${pipelineId}&$top=5&queryOrder=queueTimeDescending&api-version=7.0`;
      const response = await this.makeApiCall(url);
      const activeStatuses = ['inprogress', 'notstarted', 'postponed'];
      const runningBuild = (response.value || []).find(b => activeStatuses.includes((b.status || '').toLowerCase()));
      if (!runningBuild) return null;
      return {
        buildId: runningBuild.id,
        url: `https://dev.azure.com/${this.organization}/${this.project}/_build/results?buildId=${runningBuild.id}`,
      };
    } catch {
      return null;
    }
  }

  async triggerPipeline(pipelineId, branch = 'develop') {
    return await this.makeApiCall(`/build/builds?api-version=7.0`, {
      method: 'POST',
      body: JSON.stringify({
        definition: { id: Number(pipelineId) },
        sourceBranch: `refs/heads/${branch}`,
      })
    });
  }

  async getTestSuitesForBuild(buildId) {
    const numericBuildId = buildId.replace('build-', '');

    // Récupérer les test runs liés à ce build
    const testRunsRes = await this.makeApiCall(
      `/test/runs?buildUri=vstfs:///Build/Build/${numericBuildId}&api-version=7.1`
    );

    // Source primaire : mapping par nom de suite (fiable pour les builds planifiés et déclenchés)
    // Chaque suite Robot Framework tourne sur un environnement défini par le squad, pas par le build
    const suiteEnvMap = {
      // Nouveaux noms (pipelines per-domain)
      'rf administration-api':       'QA1',
      'rf administration-browser':   'QA1',
      'rf configuration-api':        'QA1',
      'rf configuration-browser':    'QA1',
      'rf data exploration-api':     'QA1',
      'rf data exploration-browser': 'QA1',
      'rf pelias':                   'QA1',
      'rf data preparation-api':     'QA3',
      'rf data preparation-browser': 'QA3',
      'rf investigation-api':        'QA3',
      'rf agentic-api':              'val05',
      'rf agentic-browser':          'val05',
      'rf assistant-api':            'val05',
      'rf assistant-browser':        'val05',
      'rf workspace-api':            'val05',
      'rf osint-api':                'val9',
      'rf osint-browser':            'val9',
      'rf osint':                    'val9',
      // Anciens noms (legacy / pipelines manuels)
      'robotframework ui test results de':           'QA1',
      'robotframework ui test results agentique':    'val05',
      'robotframework ui test results osint':        'val9',
      'robotframework ui test results dp':           'QA3',
      'robotframework ui test results admin & conf': 'QA1',
      'robotframework ui test results assistant':    'val05',
      'robotframework api test results de':              'QA1',
      'robotframework api test results investigation':   'QA3',
      'robotframework api test results dp':              'QA3',
      'robotframework api test results osint':           'val9',
      'robotframework api test results agentique':       'val05',
      'robotframework api test results assistant':       'val05',
      'robotframework api test results administration':  'QA1',
      'robotframework api test results configuration':   'QA1',
    };

    // Source prioritaire : templateParameters du build (dynamique, chargé une seule fois)
    const buildEnv = await this.getEnvironmentFromBuild(numericBuildId);

    const testSuites = [];

    for (const run of testRunsRes.value || []) {
      const runDetails = await this.makeApiCall(
        `/test/runs/${run.id}?api-version=7.0`
      );

      const name = run.name || `Run ${run.id}`;
      const totalTests = run.totalTests || 0;
      // notApplicableTests = tests non exécutés (SKIP Robot Framework) : ni passés ni échoués
      const skipped = run.notApplicableTests || 0;
      const passed = run.passedTests || 0;
      const failed = Math.max(0, totalTests - passed - skipped);

      const start = new Date(runDetails.startedDate);
      const end = new Date(runDetails.completedDate);
      const duration = (end - start) / 1000;

      // Priorité 1 : env dynamique depuis les templateParameters du build
      // Priorité 2 : map statique par nom de suite (fallback)
      let testEnvironment = buildEnv;
      if (!testEnvironment) {
        const nameKey = String(name).toLowerCase().trim();
        testEnvironment = suiteEnvMap[nameKey];
        if (!testEnvironment) {
          for (const [key, env] of Object.entries(suiteEnvMap)) {
            if (nameKey.includes(key)) { testEnvironment = env; break; }
          }
        }
      }

      testSuites.push({
        runId: `run-${run.id}`,
        name,
        totalTests,
        passed,
        failed,
        skipped,
        duration,
        testEnvironment: testEnvironment || 'Non défini'
      });
    }

    return {
      [`build-${numericBuildId}`]: testSuites
    };
  }

  /**
   * Résultats ignorés d'un build, avec leur motif.
   *
   * ADO n'a pas de champ « raison du skip » : le motif est le texte libre laissé par le
   * framework de test dans `errorMessage` (ce qu'écrit le SKIP Robot Framework) ou `comment`.
   * Le regroupement par motif est fait côté UI (cf. src/utils/skipReasons.js).
   *
   * Deux garde-fous de coût, cette méthode tournant au chargement de chaque dashboard :
   *  - on saute les runs dont le résumé annonce zéro ignoré (aucun résultat à télécharger) ;
   *  - on demande à ADO de ne renvoyer que les outcomes ignorés, ce qui ramène une suite de
   *    500 tests à ses quelques ignorés. Le filtre est refait côté client : si l'organisation
   *    ne l'honore pas, on reste correct, seulement plus bavard.
   */
  async getSkippedTestResultsForBuild(buildId) {
    const numericBuildId = String(buildId).replace('build-', '');
    const runs = await this.getTestRunsByBuildId(numericBuildId);
    const skipped = [];

    for (const run of runs) {
      // notApplicableTests = compteur d'ignorés du résumé de run (même source que getTestSuitesForBuild)
      if (!(run.notApplicableTests || 0)) continue;

      let results;
      try {
        results = await this.fetchAllTestResults(run.id, { outcomes: SKIPPED_OUTCOMES });
      } catch (err) {
        // Certaines organisations rejettent le paramètre `outcomes` : on retente sans filtre.
        try {
          results = await this.fetchAllTestResults(run.id);
        } catch (err2) {
          console.warn(`Erreur récupération des tests ignorés du run ${run.id}`, err2);
          continue;
        }
      }

      for (const result of results) {
        if (!isSkippedStatus(result.outcome)) continue;
        skipped.push({
          testName: result.testCaseTitle || result.automatedTestName || 'Test sans nom',
          suiteName: run.name || null,
          runId: run.id,
          outcome: result.outcome,
          errorMessage: result.errorMessage || null,
          comment: result.comment || null,
        });
      }
    }

    return skipped;
  }

  /**
   * Idem pour plusieurs builds, chaque résultat étant étiqueté avec le libellé fourni
   * (nom de squad côté vue globale, nom de suite côté page d'équipe).
   *
   * @param {Array<{buildId: string|number, label?: string}>} builds
   */
  async getSkippedTestResultsForBuilds(builds = []) {
    const perBuild = await Promise.all(
      builds.filter(b => b?.buildId).map(async ({ buildId, label }) => {
        try {
          const results = await this.getSkippedTestResultsForBuild(buildId);
          return label ? results.map(r => ({ ...r, squadName: label })) : results;
        } catch (err) {
          console.warn(`Erreur récupération des tests ignorés du build ${buildId}`, err);
          return [];
        }
      })
    );
    return perBuild.flat();
  }


  // Récupérer les données de tendance pour les graphiques
  async getTrendData(pipelineIds, days = 7, testSuiteNamesFilter = null) {
    const trendData = [];
    const today = new Date();

    // Search backwards exactly 'days' calendar days (includes weekends)
    for (let i = 0; i < days; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);

      const startOfDay = new Date(date);
      startOfDay.setUTCHours(0, 0, 0, 0);
      const endOfDay = new Date(date);
      endOfDay.setUTCHours(23, 59, 59, 999);

      try {
        let allRuns = [];
        for (const pipelineId of pipelineIds) {
          const buildsEndpoint = `/build/builds?api-version=7.0&definitions=${pipelineId}&minTime=${startOfDay.toISOString()}&maxTime=${endOfDay.toISOString()}`;
          try {
            const buildsResponse = await this.makeApiCall(buildsEndpoint);
            const builds = buildsResponse.value || [];

            for (const build of builds) {
              const runsEndpoint = `/test/runs?api-version=7.0&buildUri=${encodeURIComponent(build.uri)}`;
              try {
                const runsResponse = await this.makeApiCall(runsEndpoint);
                const runs = runsResponse.value || [];
                allRuns.push(...runs);
              } catch (runErr) {
                console.error(`Erreur récupération runs pour build ${build.id}:`, runErr);
              }
            }
          } catch (buildErr) {
            console.error(`Erreur récupération builds pour pipeline ${pipelineId}:`, buildErr);
          }
        }

        const latestRunBySuite = {};

        for (const run of allRuns) {
          const suiteName = run.name || 'default';

          if (testSuiteNamesFilter && !testSuiteNamesFilter.includes(suiteName)) {
            continue;
          }

          const currentRunDate = new Date(run.completedDate || run.lastUpdatedDate || run.startDate);
          const existing = latestRunBySuite[suiteName];
          const existingRunDate = existing ? new Date(existing.completedDate || existing.lastUpdatedDate || existing.startDate) : null;

          if (!existing || currentRunDate > existingRunDate) {
            latestRunBySuite[suiteName] = run;
          }
        }

        let totalTests = 0;
        let passedTests = 0;
        let failedTests = 0;
        let skippedTests = 0;

        for (const run of Object.values(latestRunBySuite)) {
          const t = run.totalTests || 0;
          const p = run.passedTests || 0;
          const s = run.notApplicableTests || 0;
          totalTests += t;
          passedTests += p;
          skippedTests += s;
          failedTests += t - p - s;
        }

        // Only include days that had actual executions
        if (totalTests > 0) {
          trendData.unshift({
            date: startOfDay.toISOString().split('T')[0],
            success: passedTests,
            failed: failedTests,
            skipped: skippedTests,
            total: totalTests
          });
        }
      } catch (error) {
        console.error(`❌ Erreur chargement données du ${startOfDay.toDateString()}:`, error);
      }
    }

    return trendData;
  }

  async getTrendDataForPipeline(pipelineId, days = 7) {
    const since = new Date();
    since.setDate(since.getDate() - days);
    since.setHours(0, 0, 0, 0);

    const DAY_NAMES = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const result = [];

    try {
      const endpoint = `/build/builds?api-version=7.0&definitions=${pipelineId}&minTime=${since.toISOString()}&$top=50`;
      const res = await this.makeApiCall(endpoint);
      const builds = (res.value || [])
        .filter(b => b.finishTime)
        .sort((a, b) => new Date(a.finishTime) - new Date(b.finishTime));

      for (const build of builds) {
        try {
          const runsRes = await this.makeApiCall(
            `/test/runs?api-version=7.0&buildUri=${encodeURIComponent(build.uri)}`
          );
          const runs = runsRes.value || [];

          let success = 0, failed = 0, skipped = 0, total = 0;
          for (const run of runs) {
            const t = run.totalTests || 0;
            const p = run.passedTests || 0;
            const s = run.notApplicableTests || 0;
            success += p;
            skipped += s;
            failed += t - p - s;
            total += t;
          }
          if (total === 0) continue;

          const d = new Date(build.finishTime);
          const dd = String(d.getDate()).padStart(2, '0');
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const hh = String(d.getHours()).padStart(2, '0');
          const mn = String(d.getMinutes()).padStart(2, '0');

          result.push({
            sessionLabel: `${DAY_NAMES[d.getDay()]} ${dd}/${mm} ${hh}:${mn}`,
            sessionDate: d.toISOString(),
            success,
            failed,
            skipped,
            total,
            buildNumber: build.buildNumber
          });
        } catch (e) {
          console.error(`Runs error for build ${build.id}:`, e);
        }
      }
    } catch (e) {
      console.error(`Builds error for pipeline ${pipelineId}:`, e);
    }

    return result;
  }

  async getTrendDataBySessions(pipelineIds, days = 7, testSuiteNamesFilter = null) {
    // Une session = 20h00 du jour J → 19h59 du jour J+1
    // Un build appartient à la session J si :
    //   finishTime.hour >= 20  → session key = date du build
    //   finishTime.hour < 20   → session key = date du build - 1 jour
    const since = new Date();
    since.setDate(since.getDate() - (days + 1));
    since.setHours(20, 0, 0, 0);

    const allBuildsWithRuns = [];

    for (const pipelineId of pipelineIds) {
      const endpoint = `/build/builds?api-version=7.0&definitions=${pipelineId}&minTime=${since.toISOString()}&$top=100`;
      try {
        const res = await this.makeApiCall(endpoint);
        const builds = (res.value || []).filter(b => b.finishTime);

        for (const build of builds) {
          try {
            const runsRes = await this.makeApiCall(
              `/test/runs?api-version=7.0&buildUri=${encodeURIComponent(build.uri)}`
            );
            const runs = runsRes.value || [];
            if (runs.length > 0) {
              allBuildsWithRuns.push({
                pipelineId,
                finishTime: new Date(build.finishTime),
                runs
              });
            }
          } catch (e) {
            console.error(`Runs error for build ${build.id}:`, e);
          }
        }
      } catch (e) {
        console.error(`Builds error for pipeline ${pipelineId}:`, e);
      }
    }

    // Regrouper par clé de session
    const sessionMap = {};

    for (const entry of allBuildsWithRuns) {
      const ft = entry.finishTime;
      const sessionDate = new Date(ft);
      if (ft.getHours() < 20) {
        sessionDate.setDate(sessionDate.getDate() - 1);
      }

      // Compact weekend: the display date is sessionDate + 1 day.
      // If it lands on Saturday → push session forward 2 days so it displays as Monday.
      // If it lands on Sunday  → push session forward 1 day so it displays as Monday.
      // This groups Friday-night/Saturday/Sunday/Monday-morning into a single Monday bar.
      const displayDate = new Date(sessionDate);
      displayDate.setDate(displayDate.getDate() + 1);
      if (displayDate.getDay() === 6) {        // Saturday → next Monday
        sessionDate.setDate(sessionDate.getDate() + 2);
      } else if (displayDate.getDay() === 0) { // Sunday → Monday
        sessionDate.setDate(sessionDate.getDate() + 1);
      }

      const sessionKey = sessionDate.getFullYear() + '-' +
        String(sessionDate.getMonth() + 1).padStart(2, '0') + '-' +
        String(sessionDate.getDate()).padStart(2, '0');

      if (!sessionMap[sessionKey]) {
        sessionMap[sessionKey] = { sessionKey, sessionDate, builds: [] };
      }
      sessionMap[sessionKey].builds.push(entry);
    }

    const DAY_NAMES = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const result = [];

    const sortedSessions = Object.values(sessionMap)
      .sort((a, b) => (a.sessionKey < b.sessionKey ? -1 : a.sessionKey > b.sessionKey ? 1 : 0));

    for (const session of sortedSessions) {
      const latestRunBySuite = {};
      const maxTotalBySuite = {}; // plus grand totalTests de la session, insensible à une relance partielle

      for (const buildEntry of session.builds) {
        for (const run of buildEntry.runs) {
          const suiteName = run.name || 'default';
          if (testSuiteNamesFilter && !testSuiteNamesFilter.some(f => suiteName === f || suiteName.toLowerCase().includes(f.toLowerCase()))) continue;

          maxTotalBySuite[suiteName] = Math.max(maxTotalBySuite[suiteName] || 0, run.totalTests || 0);

          const runTime = new Date(run.completedDate || run.lastUpdatedDate || run.startDate);
          const existing = latestRunBySuite[suiteName];
          if (!existing) {
            latestRunBySuite[suiteName] = run;
          } else {
            const existingTime = new Date(existing.completedDate || existing.lastUpdatedDate || existing.startDate);
            if (runTime > existingTime) latestRunBySuite[suiteName] = run;
          }
        }
      }

      let success = 0, failed = 0, skipped = 0, total = 0;
      const suites = {}; // total de tests par suite, pour suivre l'évolution du nombre de tests
      for (const [suiteName, run] of Object.entries(latestRunBySuite)) {
        const t = run.totalTests || 0;
        const p = run.passedTests || 0;
        const s = run.notApplicableTests || 0;
        suites[suiteName] = maxTotalBySuite[suiteName]; // les barres, elles, restent basées sur le dernier run
        total += t;
        success += p;
        skipped += s;
        failed += t - p - s;
      }

      if (total === 0) continue;

      const d = new Date(session.sessionDate);
      d.setDate(d.getDate() + 1);
      const dd = String(d.getDate()).padStart(2, '0');
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      result.push({
        _key: session.sessionKey,
        date: `${DAY_NAMES[d.getDay()]} ${dd}/${mm}`,
        success,
        failed,
        skipped,
        total,
        suites
      });
    }

    // Tri final par sessionKey (ISO YYYY-MM-DD, tri ASCII garanti)
    result.sort((a, b) => (a._key < b._key ? -1 : a._key > b._key ? 1 : 0));
    return result.slice(-days);
  }

  async getTrendDataFromBuilds(pipelineId) {
    const builds = await azureDevOpsService.getRecentBuildsByPipeline(pipelineId);
    const buildsForPipeline = builds[`pipeline-${pipelineId}`] || [];

    const trendData = [];

    for (const build of buildsForPipeline.slice(0, 5)) {
      const buildId = build.buildId.replace('build-', '');

      const testRunsRes = await azureDevOpsService.makeApiCall(
        `/test/runs?buildUri=vstfs:///Build/Build/${buildId}&api-version=7.1`
      );

      let passed = 0;
      let failed = 0;
      let skipped = 0;
      let total = 0;

      for (const run of testRunsRes.value || []) {
        const runTotal = run.totalTests || 0;
        const passedCount = run.passedTests || 0;
        const skippedCount = run.notApplicableTests || 0;
        total += runTotal;
        passed += passedCount;
        skipped += skippedCount;
        failed += runTotal - passedCount - skippedCount;
      }

      trendData.push({
        date: new Date(build.date).toISOString().split('T')[0],
        success: passed,
        failed: failed,
        skipped: skipped,
        total: total
      });
    }

    const reversed = trendData.reverse(); // tri du plus ancien au plus récent
    console.table(reversed); // affichage lisible
    return reversed;
  }

  // Récupérer les attachements d'un test run (pour logs complets)
  async getTestRunAttachments(runId, testResultId) {
    try {
      const endpoint = `/test/Runs/${runId}/Results/${testResultId}/attachments?api-version=7.1-preview.1`;
      const response = await this.makeApiCall(endpoint);
      return response.value || [];
    } catch (error) {
      console.error(`Erreur récupération attachements pour run ${runId}, result ${testResultId}:`, error);
      return [];
    }
  }

  // Télécharger le contenu d'un attachement
  async downloadAttachment(runId, testResultId, attachmentId) {
    try {
      const url = `${this.baseUrl}/test/Runs/${runId}/Results/${testResultId}/attachments/${attachmentId}?api-version=7.1-preview.1`;
      const response = await fetch(url, { headers: this.headers });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      const text = await response.text();
      return text;
    } catch (error) {
      console.error(`Erreur téléchargement attachement ${attachmentId}:`, error);
      return null;
    }
  }

  // Récupère TOUS les résultats d'un test run, en paginant.
  //
  // L'API /test/Runs/{id}/results ne renvoie qu'une page par appel. Sans $top/$skip,
  // un run de 465 tests n'en rendait que ~200, silencieusement : le détail affichait
  // 200 tests là où la carte en annonçait 465, et l'analyse IA ne voyait que 31 échecs
  // sur 204. On pagine donc jusqu'à épuisement.
  //
  // PAGE_SIZE reste modeste : detailsToInclude=iterations alourdit beaucoup chaque
  // résultat, et l'API plafonne de toute façon la taille de page.
  async fetchAllTestResults(apiRunId, { detailsToInclude = null, expectedTotal = null, outcomes = null } = {}) {
    const PAGE_SIZE = 200;
    const MAX_RESULTS = 20000; // garde-fou contre une boucle infinie
    const all = [];
    let skip = 0;

    for (;;) {
      const params = [
        'api-version=7.1-preview.6',
        `$top=${PAGE_SIZE}`,
        `$skip=${skip}`,
        detailsToInclude ? `detailsToInclude=${detailsToInclude}` : null,
        outcomes?.length ? `outcomes=${encodeURIComponent(outcomes.join(','))}` : null,
        `_=${Date.now()}`
      ].filter(Boolean).join('&');

      const page = (await this.makeApiCall(`/test/Runs/${apiRunId}/results?${params}`)).value || [];
      all.push(...page);

      if (page.length < PAGE_SIZE) break;
      skip += PAGE_SIZE;
      if (skip >= MAX_RESULTS) {
        console.warn(`[fetchAllTestResults] run ${apiRunId}: arrêt au garde-fou de ${MAX_RESULTS} résultats`);
        break;
      }
    }

    // Une divergence avec le résumé du run signale une troncature : on la dit plutôt
    // que de laisser l'UI afficher un sous-ensemble comme si c'était le tout.
    if (expectedTotal != null && all.length !== expectedTotal) {
      console.warn(
        `[fetchAllTestResults] run ${apiRunId}: ${all.length} résultats récupérés ` +
        `pour ${expectedTotal} annoncés par le résumé du run.`
      );
    }

    return all;
  }

  async getTestCasesForRun(runId, version = 'develop') {
    const numericRunId = runId.replace('run-', '');
    
    // Logique de calcul du runId selon la version:
    // - develop (ReadyAPI): utiliser runId tel quel pour l'API (pour avoir failingSince)
    // - 25.1 (ReadyAPI): utiliser runId tel quel
    // - robotframework: utiliser runId tel quel
    let apiRunId; // Le runId à utiliser pour l'appel API
    
    if (version === 'develop') {
      // Pour develop: on fait l'appel API avec le runId original (sans +1)
      // pour récupérer correctement failingSince
      apiRunId = parseInt(numericRunId, 10);
    } else {
      // Pour les autres versions: utiliser runId tel quel
      apiRunId = parseInt(numericRunId, 10);
    }
    
    // Récupérer les détails du run pour obtenir le build ID
    const runDetailsEndpoint = `/test/runs/${apiRunId}?api-version=7.1&_=${Date.now()}`;
    const runDetails = await this.makeApiCall(runDetailsEndpoint);
    const buildId = runDetails.build?.id || null;
    
    // Toutes les pages de résultats, avec les détails complets incluant failingSince
    const data = await this.fetchAllTestResults(apiRunId, {
      detailsToInclude: 'iterations',
      expectedTotal: runDetails.totalTests ?? null
    });
    const sampleFailed = data.find(t => t.outcome?.toLowerCase() === 'failed');
    if (sampleFailed) console.log('[getTestCasesForRun] iterationDetails sample:', JSON.stringify(sampleFailed.iterationDetails?.slice(0, 1)));

    // Normaliser la version : 25.2 est traitée comme develop en interne
    const effectiveVersion = version === '25.2' ? 'develop' : version;
    
    // Pour develop (et 25.2): récupérer aussi les résultats du runId+1 pour avoir les bons testIds dans les logs
    let testIdMap = {}; // Map: testCaseTitle -> testId from runId+1
    if (effectiveVersion === 'develop') {
      try {
      const logRunId = apiRunId + 1;
      // Paginé aussi : sans ça, les testIds des tests au-delà de la première page
      // restaient introuvables et leurs logs inaccessibles.
      const logData = await this.fetchAllTestResults(logRunId);
      
      // Créer une map testCaseTitle -> testId du run+1
      logData.forEach(test => {
        if (test.testCaseTitle) {
          testIdMap[test.testCaseTitle] = test.id;
        }
      });
      } catch (err) {
        console.warn('⚠️ Erreur lors de la récupération des testIds du run+1:', err);
      }
    }

    return data.map(test => {
      // failingSince contient des infos sur quand le test a commencé à échouer
      // Un test est "New" s'il a échoué ET que failingSince.build.id correspond au build ID du run actuel
      
      // Vérifier si c'est un nouveau failure
      // Comparer failingSince.build.id avec le buildId du run actuel
      const isNewFailure = test.outcome?.toLowerCase() === 'failed' && 
                          test.failingSince && 
                          test.failingSince.build && 
                          buildId &&
                          String(test.failingSince.build.id) === String(buildId);
      
      // Pour le logUrl: develop (et 25.2) utilisent runId+1 et testId du run+1 pour avoir plus de détails
      const logRunId = effectiveVersion === 'develop' ? apiRunId + 1 : apiRunId;
      const logTestId = effectiveVersion === 'develop' && testIdMap[test.testCaseTitle] 
        ? testIdMap[test.testCaseTitle] 
        : test.id;
      
      // Extraire le détail du step échoué depuis les iterations (ReadyAPI/SOAP)
      let stepError = null;
      const iterations = test.iterationDetails || [];
      if (iterations.length > 0) {
        const firstIter = iterations[0];
        if (firstIter.errorMessage && firstIter.errorMessage !== test.errorMessage) {
          stepError = firstIter.errorMessage;
        }
        if (!stepError && Array.isArray(firstIter.actionResults)) {
          const failedAction = firstIter.actionResults.find(
            a => a.outcome && a.outcome.toLowerCase() !== 'passed' && a.errorMessage
          );
          if (failedAction) stepError = failedAction.errorMessage;
        }
      }

      return {
        id: test.id,
        name: test.testCaseTitle,
        automatedTestName: test.automatedTestName || test.testCaseTitle,
        automatedTestStorage: test.automatedTestStorage || null,
        status: test.outcome ? test.outcome.toLowerCase() : 'unknown', // "passed", "failed", etc.
        duration: test.durationInMs ? test.durationInMs / 1000 : 0, // convert to seconds
        logUrl: `https://dev.azure.com/${this.organization}/${this.project}/_testManagement/runs?runId=${logRunId}&_a=resultSummary&resultId=${logTestId}`,
        error: test.errorMessage || null,
        stepError: stepError,
        stackTrace: test.stackTrace || null,
        isNewFailure: isNewFailure,
        failingSince: test.failingSince || null,
        // Pour récupérer le log complet via attachements
        logRunId: effectiveVersion === 'develop' ? apiRunId + 1 : apiRunId,
        logTestId: effectiveVersion === 'develop' && testIdMap[test.testCaseTitle] ? testIdMap[test.testCaseTitle] : test.id
      };
    });
  }

  // Récupérer le log complet d'un test case depuis les attachements
  async getFullTestCaseLog(testCase) {
    try {
      const { logRunId, logTestId, name } = testCase;
      if (!logRunId || !logTestId) {
        console.warn('logRunId ou logTestId manquant pour', name);
        return null;
      }

      // Récupérer les attachements du test result
      const attachments = await this.getTestRunAttachments(logRunId, logTestId);

      // Chercher le fichier log_{testTitle}.txt
      // Normaliser le nom du test pour matcher le pattern (même logique que Python sanitize_filename)
      // Remplace les caractères interdits sur Windows par underscore: < > : " / \ | ? *
      const normalizedName = name.replace(/[<>:"/\\|?*]/g, '_');
      const logFileName = `log_${normalizedName}.txt`;
      const logAttachment = attachments.find(att => 
        att.fileName && att.fileName.toLowerCase() === logFileName.toLowerCase()
      );

      if (!logAttachment) {
        console.warn(`Aucun attachement trouvé pour le pattern "${logFileName}"`);
        return null;
      }

      const logContent = await this.downloadAttachment(logRunId, logTestId, logAttachment.id);
      return logContent;
    } catch (error) {
      console.error('Erreur getFullTestCaseLog:', error);
      return null;
    }
  }

  // Récupérer les bugs pour l'area path "Fabrique\Tribus\Tribu Argonos\Squads\Squad Data Preparation"
  // qui ont l'état "New" et le tag "regression"
  async getBugsForSquadDataPreparation() {
    const areaPath = 'Fabrique\\Tribus\\Tribu Argonos\\Squads\\Squad Data Preparation';
    const wiql = `SELECT [System.Id], [System.Title], [System.State], [System.Tags], [System.AreaPath]
FROM workitems
WHERE [System.WorkItemType] = 'Bug'
  AND [System.AreaPath] = '${areaPath}'
  AND [System.State] = '1- New'
  AND [TA] = 'True'
  AND [RegressionField] = 'True'

ORDER BY [System.ChangedDate] DESC`;

    try {
      // 1) Exécuter la requête WIQL    
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      if (ids.length === 0) return [];

      // 2) Récupérer les work items en détails (API accepte jusqu'à 200 ids)
      const chunkSize = 200;
      const fields = [
        'System.Id',
        'System.Title',
        'System.State',
        'System.Tags',
        'System.AssignedTo',
        'System.CreatedBy',
        'System.CreatedDate',
        'System.ChangedDate',
        'System.AreaPath',
        'Microsoft.VSTS.Common.Severity'
      ];

      const allWorkItems = [];

      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const idsParam = chunk.join(',');
        const endpoint = `/wit/workitems?ids=${idsParam}&fields=${fields.join(',')}&api-version=7.0`;
        const res = await this.makeApiCall(endpoint);
        if (res && res.value) {
          allWorkItems.push(...res.value);
        }
      }

      return allWorkItems.map(w => ({
        id: w.id,
        title: w.fields['System.Title'],
        state: w.fields['System.State'],
        tags: w.fields['System.Tags'] || '',
        areaPath: w.fields['System.AreaPath'] || '',
        assignedTo: w.fields['System.AssignedTo'] ? w.fields['System.AssignedTo'].displayName : null,
        createdBy: w.fields['System.CreatedBy'] ? w.fields['System.CreatedBy'].displayName : null,
        createdDate: w.fields['System.CreatedDate'],
        changedDate: w.fields['System.ChangedDate'],
        severity: w.fields['Microsoft.VSTS.Common.Severity'] || null,
        url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${w.id}`
      }));

    } catch (error) {
      console.error('Erreur récupération bugs (Squad Data Preparation):', error);
      return [];
    }
  }
  

   // Récupérer les bugs pour l'area path "Fabrique\Tribus\Tribu Argonos\Squads\Squad Data Exploration"
  // qui ont l'état "New" et le tag "regression"
  async getBugsForSquadDataExploration() {
    const areaPath = 'Fabrique\\Tribus\\Tribu Argonos\\Squads\\Squad Data Exploration';
    const wiql = `SELECT [System.Id], [System.Title], [System.State], [System.Tags], [System.AreaPath]
FROM workitems
WHERE [System.WorkItemType] = 'Bug'
  AND [System.AreaPath] = '${areaPath}'
  AND [System.State] = '1- New'
  AND [TA] = 'True'
  AND [RegressionField] = 'True'

ORDER BY [System.ChangedDate] DESC`;

    try {
      // 1) Exécuter la requête WIQL    
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      if (ids.length === 0) return [];

      // 2) Récupérer les work items en détails (API accepte jusqu'à 200 ids)
      const chunkSize = 200;
      const fields = [
        'System.Id',
        'System.Title',
        'System.State',
        'System.Tags',
        'System.AssignedTo',
        'System.CreatedBy',
        'System.CreatedDate',
        'System.ChangedDate',
        'System.AreaPath',
        'Microsoft.VSTS.Common.Severity'
      ];

      const allWorkItems = [];

      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const idsParam = chunk.join(',');
        const endpoint = `/wit/workitems?ids=${idsParam}&fields=${fields.join(',')}&api-version=7.0`;
        const res = await this.makeApiCall(endpoint);
        if (res && res.value) {
          allWorkItems.push(...res.value);
        }
      }

      return allWorkItems.map(w => ({
        id: w.id,
        title: w.fields['System.Title'],
        state: w.fields['System.State'],
        tags: w.fields['System.Tags'] || '',
        areaPath: w.fields['System.AreaPath'] || '',
        assignedTo: w.fields['System.AssignedTo'] ? w.fields['System.AssignedTo'].displayName : null,
        createdBy: w.fields['System.CreatedBy'] ? w.fields['System.CreatedBy'].displayName : null,
        createdDate: w.fields['System.CreatedDate'],
        changedDate: w.fields['System.ChangedDate'],
        severity: w.fields['Microsoft.VSTS.Common.Severity'] || null,
        url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${w.id}`
      }));

    } catch (error) {
      console.error('Erreur récupération bugs (Squad Data Preparation):', error);
      return [];
    }
  }

  // Récupérer les bugs pour toutes les squads "Fabrique\Tribus\Tribu Argonos\Squads\Squad *"
  // qui ont l'état "New" et le tag "regression"
  async getBugsForAllSquads() {
    const severityRank = {
  "1 - Critical": 1,
  "2 - High": 2,
  "3 - Medium": 3,
  "4 - Low": 4 };

    const wiql = `SELECT [System.Id], [System.Title], [System.State], [System.Tags], [System.AreaPath]
FROM workitems
WHERE [System.WorkItemType] = 'Bug'
  AND [System.State] <> ''
  AND [System.State] <> '6- Done'
  AND [System.State] <> '6- Cancelled'
  AND [System.State] <> 'Task - Done'
  AND [System.State] <> '5- Validation'
  AND [System.State] <> '4- Doc'
  AND [Custom.TA] = True
ORDER BY [System.Id] DESC`;

    try {
      // 1) Exécuter la requête WIQL    
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      if (ids.length === 0) return [];

      // 2) Récupérer les work items en détails (API accepte jusqu'à 200 ids)
      const chunkSize = 200;
      const fields = [
        'System.Id',
        'System.Title',
        'System.State',
        'System.Tags',
        'System.AssignedTo',
        'System.CreatedBy',
        'System.CreatedDate',
        'System.ChangedDate',
        'System.AreaPath',
        'Microsoft.VSTS.Common.Severity'
      ];

      const allWorkItems = [];

      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const idsParam = chunk.join(',');
        const endpoint = `/wit/workitems?ids=${idsParam}&fields=${fields.join(',')}&api-version=7.0`;
        const res = await this.makeApiCall(endpoint);
        if (res && res.value) {
          allWorkItems.push(...res.value);
        }
      }

      return allWorkItems.map(w => ({
        id: w.id,
        title: w.fields['System.Title'],
        state: w.fields['System.State'],
        tags: w.fields['System.Tags'] || '',
        areaPath: w.fields['System.AreaPath'] || '',
        assignedTo: w.fields['System.AssignedTo'] ? w.fields['System.AssignedTo'].displayName : null,
        createdBy: w.fields['System.CreatedBy'] ? w.fields['System.CreatedBy'].displayName : null,
        createdDate: w.fields['System.CreatedDate'],
        changedDate: w.fields['System.ChangedDate'],
        severity: w.fields['Microsoft.VSTS.Common.Severity'] || null,
        url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${w.id}`
      })).sort((a, b) => severityRank[a.severity]- severityRank[b.severity]);

    } catch (error) {
      console.error('Erreur récupération All bugs :', error);
      return [];
    }
  }
    // Récupérer les bugs critiques pour toutes les squads
  // qui ont l'état "New" et le tag "regression"
 
  // 🔁 Récupération de la timeline du build
  async getBuildTimeline(buildId) {
    try {
      const endpoint = `/build/builds/${buildId}/timeline?api-version=7.1`;
      const data = await this.makeApiCall(endpoint);
      return data.records || [];
    } catch (error) {
      console.warn(`Timeline non disponible pour le build ${buildId}:`, error.message);
      return [];
    }
  }

  // Récupérer les changements (commits) associés à un build
  async getBuildChanges(buildId, top = 100) {
    try {
      const endpoint = `/build/builds/${buildId}/changes?api-version=7.0&$top=${top}`;
      const data = await this.makeApiCall(endpoint);
      return data.value || [];
    } catch (error) {
      console.error(`Erreur récupération changes du build ${buildId}:`, error);
      return [];
    }
  }

  // 🔁 Récupération d’un log brut par ID (retourne du texte brut)
  async getLogById(buildId, logId) {
    // Endpoint principal pour récupérer le contenu brut du log
    const baseEndpoint = `/build/builds/${buildId}/logs/${logId}`;
    const primaryEndpoint = `${baseEndpoint}?api-version=7.1-preview.2`;
    const downloadEndpoint = `${baseEndpoint}?download=true&api-version=7.1-preview.2`;

    const makeFetch = async (endpointLabel, fullEndpoint) => {
      const url = `${this.baseUrl}${fullEndpoint}`;
      try {
        const response = await fetch(url, {
          headers: {
            ...this.headers,
            // Forcer Accept pour éviter certains proxies qui bloquent le plain text
            'Accept': 'text/plain, */*'
          },
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status} ${response.statusText}`);
        }
        const text = await response.text();
        return text;
      } catch (e) {
        console.warn(`[getLogById] (${endpointLabel}) erreur:`, e.message);
        throw e;
      }
    };

    // Essai principal
    try {
      return await makeFetch('primary', primaryEndpoint);
    } catch (primaryErr) {
      // Fallback avec download=true (certaines orga nécessitent ce paramètre pour du contenu complet)
      try {
        return await makeFetch('download', downloadEndpoint);
      } catch (downloadErr) {
        console.error('[getLogById] échecs successifs primary + download:', primaryErr.message, downloadErr.message);
        throw downloadErr; // remonter la dernière erreur
      }
    }
  }

  // 🧩 Récupération des logs liés aux étapes de test d’un build donné
  async getTestLogsFromBuild(buildId, stepNameFilter = 'ReadyAPI') {
    const numericBuildId = buildId.replace('build-', '');
    const timeline = await this.getBuildTimeline(numericBuildId);

    const testSteps = timeline.filter(
      (step) =>
        step.type === 'Task' &&
        step.name.toLowerCase().includes(stepNameFilter.toLowerCase()) &&
        step.log?.id !== undefined
    );

    if (!testSteps || testSteps.length === 0) {
      console.warn(`[getTestLogsFromBuild] Aucun step avec logId trouvé pour le filtre "${stepNameFilter}" dans le build ${buildId}`);
      return [{
        stepName: null,
        logId: null,
        content: '',
        error: `Aucun log trouvé pour le filtre "${stepNameFilter}" dans le build ${buildId}.`
      }];
    }

    const logs = [];
    for (const step of testSteps) {
      try {
        const logText = await this.getLogById(numericBuildId, step.log.id);
        logs.push({
          stepName: step.name,
          logId: step.log.id,
          content: logText,
          error: null
        });
      } catch (err) {
        console.error(`[getTestLogsFromBuild] Erreur récupération logId=${step.log.id} pour step="${step.name}":`, err);
        logs.push({
          stepName: step.name,
          logId: step.log.id,
          content: '',
          error: `Erreur lors de la récupération du log: ${err.message}`
        });
      }
    }
    return logs;
  }

  async launchTestCase(testCaseName, testSuiteName) {
    const getTestSuiteNameAndPipelineId =(testSuiteName) => {
      switch (testSuiteName) {
        case 'DP - smoke tests':
          return { suiteName: 'SmokeTest', pipelineId: 503, env: 'qa3' };
        case 'DP - Regression Test':
          return { suiteName: 'Regression', pipelineId: 503, env: 'qa3' };
        case 'DP - Absimilia Test Processus':
          return { suiteName: 'Abssimilia Process', pipelineId: 503, env: 'qa3' };
        case 'DP - Clean Up DP':
          return { suiteName: 'DP Clean Up', pipelineId: 503, env: 'qa3' };
        case 'DE - Cleanup':
          return { suiteName: 'Cleanup', pipelineId: 504, env: 'QA1-Unified' };
        case 'DE - smoke tests':
          return { suiteName: 'Smoke Tests', pipelineId: 504, env: 'QA1-Unified' };
        case 'DE - Regression Tests Explo':
          return { suiteName: 'Regression Tests', pipelineId: 504, env: 'QA1-Unified' };
        case 'DE - Admin':
          return { suiteName: 'Admin', pipelineId: 504, env: 'QA1-Unified' };
        case 'Framework - smoke tests':
          return { suiteName: 'Smoke Tests', pipelineId: 502, env: 'unified qa1' };
        case 'Framework - Regression Test':
          return { suiteName: 'Test Regression', pipelineId: 502, env: 'unified qa1' };
        case 'Inv - Critical Test':
          return { suiteName: 'Critical Test', pipelineId: 505, env: 'qa3' };
        case 'Inv - Major Test':
          return { suiteName: 'Major Test', pipelineId: 505, env: 'qa3' };
        default:
          throw new Error(`Test suite inconnue : ${testSuiteName}`);
      }

    };
    const { suiteName, pipelineId, env } = getTestSuiteNameAndPipelineId(testSuiteName);
    const endpoint = `/pipelines/${pipelineId}/runs?api-version=7.1-preview.1`;
    
    // Essai format: templateParameters comme objet JSON (non stringifié)
    const body = {
      resources: {
        repositories: {
          self: {
            refName: 'refs/heads/master'
          }
        }
      },
      templateParameters: {
        envadm: env,
        brancheadm: 'develop',
        testsuite: suiteName,
        testcase: testCaseName
      }
    };
    
    // Debug: log la demande complète
    if (this.debug) {
      console.log(`[launchTestCase] POST ${endpoint}`);
      console.log('[launchTestCase] Body:', JSON.stringify(body, null, 2));
    }

    try {
      const data = await this.makeApiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: {
          ...this.headers,
          'Content-Type': 'application/json',
        }
      });

      if (data.error || data.message) {
        console.error('Erreur dans la réponse:', data);
        return { success: false, error: data.message || data.error };
      }
      
      const runId = data.id;
      const runUrl = `https://dev.azure.com/${this.organization}/${this.project}/_build/results?buildId=${runId}`;
      return { success: true, runId, runUrl, pipelineId };

    } catch (err) {
      console.error('Erreur lors du déclenchement:', err);
      return { success: false, error: err.message };
    }
  }

  async getPipelineStatus(runId) {
    // Bypass ApiCache for live polling by adding a cache-busting query param
    const endpoint = `/build/builds/${runId}?api-version=7.1-preview.7&_=${Date.now()}`;

    try {
      const response = await this.makeApiCall(endpoint);

      if (response.error || response.message) {
        console.error('Erreur récupération statut pipeline');
        return null;
      }
      
      return {
        status: response.status,         // "inProgress", "completed", etc.
        result: response.result || null, // "succeeded", "failed", "canceled", null
      };      
    } catch (err) {
      console.error('Erreur réseau:', err);
      return null;
    }
  }

  // Récupérer l'historique des modifications d'un work item
  async getWorkItemUpdates(workItemId) {
    const endpoint = `/wit/workitems/${workItemId}/updates?api-version=7.0`;
    
    try {
      const response = await this.makeApiCall(endpoint);
      
      if (response.error || response.message) {
        console.error(`Erreur récupération historique work item ${workItemId}`);
        return [];
      }
      
      return response.value || [];
    } catch (err) {
      console.error('Erreur réseau:', err);
      return [];
    }
  }

  // Trouver la date de résolution réelle d'un bug basée sur son historique
  async getBugResolutionDate(bugId, currentState) {
    const updates = await this.getWorkItemUpdates(bugId);
    
    console.log(`Bug ${bugId} (${currentState}): ${updates.length} updates trouvés`);
    
    // Debug: afficher la structure du premier update avec un changement de state
    const stateUpdate = updates.find(u => u.fields && u.fields['System.State']);
    if (stateUpdate && updates.indexOf(stateUpdate) === 0) {
      console.log('Structure update exemple:', JSON.stringify(stateUpdate, null, 2));
    }
    
    // Pour un bug "6- Done", chercher d'abord quand il est passé à "5- Validation"
    // Sinon quand il est passé à "6- Done"
    if (currentState === '6- Done') {
      // Chercher d'abord 5- Validation
      const validationUpdate = updates.find(update => 
        update.fields && 
        update.fields['System.State'] && 
        update.fields['System.State'].newValue === '5- Validation'
      );
      
      if (validationUpdate) {
        const dateObj = validationUpdate.fields['System.ChangedDate'] || validationUpdate.revisedDate;
        const date = dateObj?.newValue || dateObj;
        console.log(`Bug ${bugId}: Date de validation trouvée: ${date}`);
        return date;
      }
      
      // Sinon chercher 6- Done
      const doneUpdate = updates.find(update => 
        update.fields && 
        update.fields['System.State'] && 
        update.fields['System.State'].newValue === '6- Done'
      );
      
      if (doneUpdate) {
        const dateObj = doneUpdate.fields['System.ChangedDate'] || doneUpdate.revisedDate;
        const date = dateObj?.newValue || dateObj;
        console.log(`Bug ${bugId}: Date de Done trouvée: ${date}`);
        return date;
      }
    }
    
    // Pour "5- Validation" ou "6- Cancelled", chercher quand il est passé à cet état
    if (currentState === '5- Validation' || currentState === '6- Cancelled') {
      const stateUpdate = updates.find(update => 
        update.fields && 
        update.fields['System.State'] && 
        update.fields['System.State'].newValue === currentState
      );
      
      if (stateUpdate) {
        const dateObj = stateUpdate.fields['System.ChangedDate'] || stateUpdate.revisedDate;
        const date = dateObj?.newValue || dateObj;
        console.log(`Bug ${bugId}: Date de ${currentState} trouvée: ${date}`);
        return date;
      }
    }
    
    console.warn(`Bug ${bugId}: Aucune date de résolution trouvée dans l'historique`);
    
    // Si rien trouvé, retourner null
    return null;
  }

  // Récupérer les releases pour une définition de release
  async getReleasesForDefinition(releaseDefinitionId, top = 10) {
    // Note: L'API Release Management utilise un baseUrl différent (vsrm au lieu de dev)
    const releaseBaseUrl = `https://vsrm.dev.azure.com/${this.organization}/${this.project}`;
    const endpoint = `/_apis/release/releases?definitionId=${releaseDefinitionId}&$top=${top}&$expand=environments&api-version=7.1-preview.8`;
    
    try {
      const url = `${releaseBaseUrl}${endpoint}`;
      const response = await fetch(url, {
        headers: this.headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data.value || [];
    } catch (error) {
      console.error('Erreur récupération releases:', error);
      return [];
    }
  }

  // Récupérer les commits récents d'un repository
  async getRecentCommitsForRepo(repoName, top = 10) {
    try {
      // 1. Récupérer la liste des repos pour trouver l'ID
      const repos = await this.getRepositories();
      const repo = repos.find(r => r.name === repoName);
      
      if (!repo) {
        console.warn(`Repository ${repoName} non trouvé`);
        return [];
      }

      // 2. Récupérer les commits du repo
      const endpoint = `/git/repositories/${repo.id}/commits?$top=${top}&api-version=7.1`;
      const response = await this.makeApiCall(endpoint);
      
      return response.value || [];
    } catch (error) {
      console.error(`Erreur récupération commits pour ${repoName}:`, error);
      return [];
    }
  }

  // Récupérer les commits d'un repository entre deux dates
  async getCommitsForRepoByDateRange(repoName, fromDate, toDate, branchName = null) {
    try {
      // 1. Récupérer la liste des repos pour trouver l'ID
      const repos = await this.getRepositories();
      const repo = repos.find(r => r.name === repoName);
      
      if (!repo) {
        console.warn(`Repository ${repoName} non trouvé`);
        return [];
      }

      // 2. Récupérer tous les commits de la branche (ou toutes les branches si non spécifié)
      let allCommits = [];
      let skip = 0;
      const top = 100;
      let hasMore = true;
      
      // Construction de l'URL avec la branche si spécifiée
      let baseEndpoint = `/git/repositories/${repo.id}/commits?`;
      const params = new URLSearchParams({
        '$top': top.toString(),
        'api-version': '7.1'
      });
      
      // Ajouter le filtre de branche si spécifié (format: refs/heads/branch-name)
      if (branchName) {
        params.append('searchCriteria.itemVersion.version', branchName);
        params.append('searchCriteria.itemVersion.versionType', 'branch');
      }
      
      // Récupérer les commits par pages
      while (hasMore && allCommits.length < 500) {
        params.set('$skip', skip.toString());
        const endpoint = `${baseEndpoint}${params.toString()}`;
        const response = await this.makeApiCall(endpoint);
        
        if (response.value && response.value.length > 0) {
          allCommits = allCommits.concat(response.value);
          skip += top;
          hasMore = response.value.length === top;
        } else {
          hasMore = false;
        }
      }
      
      // 3. Filtrer les commits par date côté client (car l'API ne filtre pas correctement)
      const fromDateTime = new Date(fromDate).getTime();
      const toDateTime = new Date(toDate).getTime();
      
      const filteredCommits = allCommits.filter(commit => {
        const commitDate = new Date(commit.author.date || commit.committer.date).getTime();
        return commitDate >= fromDateTime && commitDate <= toDateTime;
      });
      
      return filteredCommits;
    } catch (error) {
      console.error(`Erreur récupération commits pour ${repoName}:`, error);
      return [];
    }
  }

  // Récupérer les builds d'un pipeline avec leur timeline
  async getBuildWithTimeline(buildId) {
    try {
      const build = await this.makeApiCall(`/build/builds/${buildId}?api-version=7.0`);
      const timeline = await this.getBuildTimeline(buildId);
      
      // Extraire les stages du timeline
      const stages = timeline.filter(record => record.type === 'Stage');
      
      // Trouver le stage de déploiement (généralement nommé "Deploy" ou similaire)
      const deployStage = stages.find(s => 
        s.name.toLowerCase().includes('deploy') || 
        s.name.toLowerCase().includes('déploie') ||
        s.name.toLowerCase().includes('release')
      );
      
      return {
        ...build,
        stages: stages,
        deployStage: deployStage,
        buildStatus: build.result || build.status,
        deployStatus: deployStage ? (deployStage.result || deployStage.state) : null
      };
    } catch (error) {
      console.error(`Erreur récupération build avec timeline:`, error);
      return null;
    }
  }

  // Récupérer les derniers builds d'un pipeline avec leurs timelines
  async getBuildsWithTimeline(pipelineId, top = 20) {
    try {
      const endpoint = `/build/builds?definitions=${pipelineId}&$top=${top}&queryOrder=finishTimeDescending&api-version=7.0`;
      const response = await this.makeApiCall(endpoint);
      
      const buildsWithTimeline = await Promise.all(
        (response.value || []).map(async (build) => {
          try {
            const timeline = await this.getBuildTimeline(build.id);
            const stages = timeline.filter(record => record.type === 'Stage');
            
            const deployStage = stages.find(s => 
              s.name.toLowerCase().includes('deploy') || 
              s.name.toLowerCase().includes('déploie') ||
              s.name.toLowerCase().includes('release')
            );
            
            return {
              ...build,
              stages: stages,
              deployStage: deployStage,
              buildStatus: build.result || build.status,
              deployStatus: deployStage ? (deployStage.result || deployStage.state) : null
            };
          } catch (error) {
            console.warn(`Impossible de récupérer la timeline pour le build ${build.id}:`, error.message);
            // Retourner le build sans timeline en cas d'erreur
            return {
              ...build,
              stages: [],
              deployStage: null,
              buildStatus: build.result || build.status,
              deployStatus: null
            };
          }
        })
      );
      
      return buildsWithTimeline;
    } catch (error) {
      console.error(`Erreur récupération builds avec timeline:`, error);
      return [];
    }
  }

  // Récupérer les Product Backlog Items pour Squad Socle Front et calculer MTTD (2- Dev à 6- Done)
  async getSquadSocleFrontPBIs() {
    const wiql = `SELECT [System.Id], [System.Title], [System.State], [System.AreaPath], [System.CreatedDate]
FROM workitems
WHERE [System.WorkItemType] = 'Product Backlog Item'
  AND [System.AreaPath] UNDER 'Fabrique\\Tribus\\Tribu Argonos\\Squads\\Squad Framework Front'`;

    try {
      // 1) Exécuter la requête WIQL
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      if (ids.length === 0) return [];

      // 2) Récupérer les work items en détails
      const chunkSize = 200;
      const fields = [
        'System.Id',
        'System.Title',
        'System.State',
        'System.AreaPath',
        'System.CreatedDate',
        'System.ChangedDate'
      ];

      const allWorkItems = [];
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const idsParam = chunk.join(',');
        const endpoint = `/wit/workitems?ids=${idsParam}&fields=${fields.join(',')}&api-version=7.0`;
        const res = await this.makeApiCall(endpoint);
        if (res && res.value) {
          allWorkItems.push(...res.value);
        }
      }

      return allWorkItems.map(w => ({
        id: w.id,
        title: w.fields['System.Title'],
        state: w.fields['System.State'],
        areaPath: w.fields['System.AreaPath'] || '',
        createdDate: w.fields['System.CreatedDate'],
        changedDate: w.fields['System.ChangedDate'],
        url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${w.id}`
      }));
    } catch (error) {
      console.error('Erreur récupération PBIs Squad Socle Front:', error);
      return [];
    }
  }

  // Récupérer les bugs créés et fermés pour une squad dans les derniers mois
  async getBugsCreatedAndClosedByAreaPath(areaPath, fromMonthsAgo = 6) {
    // Calculer la date limite
    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - fromMonthsAgo);
    const fromDateStr = fromDate.toISOString().split('T')[0];

    const wiql = `SELECT [System.Id], [System.Title], [System.State], [System.AreaPath], [System.CreatedDate], [System.ChangedDate]
FROM workitems
WHERE [System.WorkItemType] = 'Bug'
  AND [System.AreaPath] UNDER '${areaPath}'
  AND [System.CreatedDate] >= '${fromDateStr}'`;

    try {
      // 1) Exécuter la requête WIQL
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      if (ids.length === 0) return { created: [], closed: [] };

      // 2) Récupérer les work items en détails
      const chunkSize = 200;
      const fields = [
        'System.Id',
        'System.Title',
        'System.State',
        'System.AreaPath',
        'System.CreatedDate',
        'System.ChangedDate'
      ];

      const allWorkItems = [];
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const idsParam = chunk.join(',');
        const endpoint = `/wit/workitems?ids=${idsParam}&fields=${fields.join(',')}&api-version=7.0`;
        const res = await this.makeApiCall(endpoint);
        if (res && res.value) {
          allWorkItems.push(...res.value);
        }
      }

      // Formater les bugs
      const bugs = allWorkItems.map(w => ({
        id: w.id,
        title: w.fields['System.Title'],
        state: w.fields['System.State'],
        areaPath: w.fields['System.AreaPath'] || '',
        createdDate: w.fields['System.CreatedDate'],
        changedDate: w.fields['System.ChangedDate'],
        url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${w.id}`
      }));

      // Séparer créés et fermés
      const created = bugs;
      const closed = bugs.filter(bug => {
        const closedStates = ['6- Done', '6- Cancelled', '5- Validation'];
        return closedStates.includes(bug.state) && 
               new Date(bug.changedDate) >= fromDate;
      });

      return { created, closed };
    } catch (error) {
      console.error('Erreur récupération bugs par area path:', error);
      return { created: [], closed: [] };
    }
  }

  // Obtenir la date du changement de statut pour une PBI (2- Dev à 6- Done)
  async getPBIStatusChangeDate(pbiId, targetStatus) {
    const updates = await this.getWorkItemUpdates(pbiId);
    
    // Chercher quand la PBI est passée au statut cible
    const statusUpdate = updates.find(update => 
      update.fields && 
      update.fields['System.State'] && 
      update.fields['System.State'].newValue === targetStatus
    );
    
    if (statusUpdate) {
      const dateObj = statusUpdate.fields['System.ChangedDate'] || statusUpdate.revisedDate;
      const date = dateObj?.newValue || dateObj;
      return date;
    }
    
    return null;
  }

  // Récupérer les work items complétés (PBI, Demande Client, Dette Technique) pour une période donnée
  async getCompletedWorkItems(startDate, endDate, areaPath) {
    // Formater les dates au format YYYY-MM-DD uniquement (sans l'heure)
    const start = startDate; // '2025-08-01'
    const end = endDate;     // '2025-12-31'

    // Requête WIQL pour récupérer les PBI, Demande Client et Dette Technique
    // passés à "Done" dans la période spécifiée sous le area path donné
    const wiql = `SELECT [System.Id], [System.Title], [System.WorkItemType], [System.State], [System.AreaPath], [Microsoft.VSTS.Common.ClosedDate]
FROM WorkItems
WHERE [System.TeamProject] = 'Fabrique'
  AND [System.WorkItemType] IN ('Product Backlog Item', 'Demande Client', 'Dette technique', 'Bug')
  AND [Microsoft.VSTS.Common.ClosedDate] >= '${start}'
  AND [Microsoft.VSTS.Common.ClosedDate] <= '${end}'
  AND [System.AreaPath] UNDER 'Fabrique\\Tribus\\Tribu Argonos'
ORDER BY [Microsoft.VSTS.Common.ClosedDate] DESC`;

    try {
      // Exécuter la requête WIQL
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      if (ids.length === 0) return { pbiCount: 0, demandeClientCount: 0, detteTechniqueCount: 0, items: [] };

      // Récupérer les détails des work items
      const chunkSize = 200;
      const fields = [
        'System.Id',
        'System.Title',
        'System.WorkItemType',
        'System.State',
        'System.AreaPath',
        'Microsoft.VSTS.Common.ClosedDate',
        'System.CreatedDate'
      ];

      const allWorkItems = [];
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const idsParam = chunk.join(',');
        const endpoint = `/wit/workitems?ids=${idsParam}&fields=${fields.join(',')}&api-version=7.0`;
        const res = await this.makeApiCall(endpoint);
        if (res && res.value) {
          allWorkItems.push(...res.value);
        }
      }

      // Mapper les résultats
      const items = allWorkItems.map(w => ({
        id: w.id,
        title: w.fields['System.Title'],
        workItemType: w.fields['System.WorkItemType'],
        state: w.fields['System.State'],
        areaPath: w.fields['System.AreaPath'] || '',
        closedDate: w.fields['Microsoft.VSTS.Common.ClosedDate'],
        createdDate: w.fields['System.CreatedDate'],
        url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${w.id}`
      }));

      // Compter par type
      const pbiCount = items.filter(item => item.workItemType === 'Product Backlog Item').length;
      const demandeClientCount = items.filter(item => item.workItemType === 'Demande Client').length;
      const detteTechniqueCount = items.filter(item => item.workItemType === 'Dette technique').length;
      const bugCount = items.filter(item => item.workItemType === 'Bug').length;

      return {
        pbiCount,
        demandeClientCount,
        detteTechniqueCount,
        bugCount,
        totalCount: items.length,
        items
      };
    } catch (error) {
      console.error('Erreur récupération work items complétés:', error);
      return { pbiCount: 0, demandeClientCount: 0, detteTechniqueCount: 0, bugCount: 0, totalCount: 0, items: [] };
    }
  }

  // Récupérer les Demande Client (optionnel: filtre date, area path, état mini et champs additionnels)
  async getDemandeClientWorkItems({ startDate, endDate, areaPath, typeFieldName, minState, automatedTestsStatusFieldName }) {
    const dateFilters = [];
    if (startDate) {
      dateFilters.push(`[System.CreatedDate] >= '${startDate}'`);
    }
    if (endDate) {
      dateFilters.push(`[System.CreatedDate] <= '${endDate}'`);
    }

    const areaFilter = areaPath ? `AND [System.AreaPath] UNDER '${areaPath}'` : '';
    const dateFilterText = dateFilters.length ? `AND ${dateFilters.join(' AND ')}` : '';
    const stateFilter = minState ? `AND [System.State] >= '${minState}'` : '';

    const wiql = `SELECT [System.Id], [System.Title], [System.State], [System.CreatedDate], [System.IterationPath]
FROM WorkItems
WHERE [System.TeamProject] = 'Fabrique'
  AND [System.WorkItemType] = 'Demande Client'
  ${areaFilter}
  ${stateFilter}
  ${dateFilterText}
ORDER BY [System.CreatedDate] DESC`;

    try {
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      if (ids.length === 0) return [];

      const chunkSize = 200;
      const baseFields = [
        'System.Id',
        'System.Title',
        'System.State',
        'System.CreatedDate',
        'System.IterationPath',
        'System.AreaPath',
        'Microsoft.VSTS.Common.ClosedDate'
      ];

      const fetchItems = async (fieldsList) => {
        const allWorkItems = [];
        for (let i = 0; i < ids.length; i += chunkSize) {
          const chunk = ids.slice(i, i + chunkSize);
          const idsParam = chunk.join(',');
          const endpoint = `/wit/workitems?ids=${idsParam}&fields=${fieldsList.join(',')}&api-version=7.0`;
          const res = await this.makeApiCall(endpoint);
          if (res && res.value) {
            allWorkItems.push(...res.value);
          }
        }
        return allWorkItems;
      };

      let allWorkItems = [];
      try {
        const optionalFields = [typeFieldName, automatedTestsStatusFieldName].filter(Boolean);
        const fields = [...baseFields, ...optionalFields.filter((field, index, array) => array.indexOf(field) === index)];
        allWorkItems = await fetchItems(fields);
      } catch (fieldError) {
        if (typeFieldName || automatedTestsStatusFieldName) {
          console.warn('Champ additionnel invalide, retry sans champs optionnels:', {
            typeFieldName,
            automatedTestsStatusFieldName,
            error: fieldError
          });
          allWorkItems = await fetchItems(baseFields);
        } else {
          throw fieldError;
        }
      }

      return allWorkItems.map(w => ({
        id: w.id,
        title: w.fields['System.Title'],
        state: w.fields['System.State'],
        createdDate: w.fields['System.CreatedDate'],
        closedDate: w.fields['Microsoft.VSTS.Common.ClosedDate'] || null,
        iterationPath: w.fields['System.IterationPath'] || '',
        areaPath: w.fields['System.AreaPath'] || '',
        type: typeFieldName ? (w.fields[typeFieldName] || null) : null,
        automatedTestsStatus: automatedTestsStatusFieldName ? (w.fields[automatedTestsStatusFieldName] || null) : null,
        url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${w.id}`
      }));
    } catch (error) {
      console.error('Erreur recuperation demandes client:', error);
      return [];
    }
  }

  async getPlannedReleaseFieldReferenceName() {
    try {
      const response = await this.makeApiCall('/wit/fields?api-version=7.0');
      const fields = Array.isArray(response.value) ? response.value : [];

      const candidates = fields
        .filter(field => {
          const name = String(field.name || '').toLowerCase();
          const referenceName = String(field.referenceName || '').toLowerCase();

          return (
            (name.includes('planned') && name.includes('release')) ||
            referenceName.includes('plannedrelease') ||
            referenceName.includes('planned_release') ||
            referenceName.includes('planned.release')
          );
        })
        .sort((left, right) => {
          const leftRef = String(left.referenceName || '').toLowerCase();
          const rightRef = String(right.referenceName || '').toLowerCase();

          if (leftRef.startsWith('custom.') && !rightRef.startsWith('custom.')) return -1;
          if (!leftRef.startsWith('custom.') && rightRef.startsWith('custom.')) return 1;
          return leftRef.localeCompare(rightRef);
        });

      return candidates[0]?.referenceName || null;
    } catch (error) {
      console.warn('Impossible de resoudre automatiquement le champ Planned Release:', error);
      return null;
    }
  }

  async getWorkItemsByPlannedReleaseContains(plannedReleaseValue, { areaPath = 'Fabrique', tagFilter = null } = {}) {
    const safeReleaseValue = String(plannedReleaseValue || '').trim();
    if (!safeReleaseValue) {
      return { items: [], plannedReleaseFieldName: null };
    }

    const escapeWiqlString = value => String(value).replace(/'/g, "''");
    const detectedFieldName = await this.getPlannedReleaseFieldReferenceName();
    const candidateFieldNames = Array.from(new Set([
      detectedFieldName,
      'Custom.PlannedRelease',
      'Custom.Planned_Release',
      'Custom.Planned.Release'
    ].filter(Boolean)));

    const safeTag = tagFilter ? String(tagFilter).trim() : null;
    const tagClause = safeTag ? `\n  AND [System.Tags] CONTAINS '${escapeWiqlString(safeTag)}'` : '';

    let lastError = null;

    for (const plannedReleaseFieldName of candidateFieldNames) {
      try {
        const wiql = `SELECT [System.Id]
FROM WorkItems
WHERE [System.TeamProject] = '${escapeWiqlString(this.project)}'
  AND [System.AreaPath] UNDER '${escapeWiqlString(areaPath)}'
  AND [${plannedReleaseFieldName}] CONTAINS '${escapeWiqlString(safeReleaseValue)}'${tagClause}
ORDER BY [System.CreatedDate] DESC`;

        const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
          method: 'POST',
          body: JSON.stringify({ query: wiql })
        });

        const ids = (wiqlResponse.workItems || []).map(workItem => workItem.id).filter(Boolean);
        if (ids.length === 0) {
          return { items: [], plannedReleaseFieldName };
        }

        const chunkSize = 200;
        const fields = [
          'System.Id',
          'System.Title',
          'System.State',
          'System.WorkItemType',
          'System.AreaPath',
          'System.CreatedDate',
          'Microsoft.VSTS.Common.ClosedDate',
          'Microsoft.VSTS.Common.Priority',
          'Microsoft.VSTS.Common.Severity',
          plannedReleaseFieldName
        ];

        const allWorkItems = [];
        for (let i = 0; i < ids.length; i += chunkSize) {
          const chunk = ids.slice(i, i + chunkSize);
          const endpoint = `/wit/workitems?ids=${chunk.join(',')}&fields=${fields.join(',')}&api-version=7.0`;
          const response = await this.makeApiCall(endpoint);
          if (response && response.value) {
            allWorkItems.push(...response.value);
          }
        }

        return {
          plannedReleaseFieldName,
          items: allWorkItems.map(workItem => ({
            id: workItem.id,
            title: workItem.fields['System.Title'] || '',
            state: workItem.fields['System.State'] || '',
            workItemType: workItem.fields['System.WorkItemType'] || 'Non renseigne',
            areaPath: workItem.fields['System.AreaPath'] || 'Non renseigne',
            createdDate: workItem.fields['System.CreatedDate'] || null,
            closedDate: workItem.fields['Microsoft.VSTS.Common.ClosedDate'] || null,
            priority: workItem.fields['Microsoft.VSTS.Common.Priority'] ?? null,
            severity: workItem.fields['Microsoft.VSTS.Common.Severity'] || null,
            plannedRelease: workItem.fields[plannedReleaseFieldName] || '',
            url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${workItem.id}`
          }))
        };
      } catch (error) {
        lastError = error;
        console.warn(`Champ Planned Release inutilisable: ${plannedReleaseFieldName}`, error);
      }
    }

    if (lastError) {
      throw lastError;
    }

    throw new Error('Aucun champ Planned Release exploitable n\'a ete trouve.');
  }

  parsePullRequestArtifactUrl(artifactUrl) {
    const marker = 'vstfs:///Git/PullRequestId/';
    if (!artifactUrl || !artifactUrl.includes(marker)) return null;

    const encoded = artifactUrl.substring(artifactUrl.indexOf(marker) + marker.length);
    const decoded = decodeURIComponent(encoded);
    const parts = decoded.split('/');
    if (parts.length < 3) return null;

    const repoId = parts[1];
    const prId = Number(parts[2]);
    if (!repoId || Number.isNaN(prId)) return null;

    return { repoId, prId };
  }

  async getPullRequestById(repoId, prId) {
    try {
      return await this.makeApiCall(`/git/repositories/${repoId}/pullRequests/${prId}?api-version=7.1-preview.1`);
    } catch (error) {
      console.warn(`Erreur recuperation PR ${prId} repo=${repoId}:`, error);
      return null;
    }
  }

  async getLinkedPullRequestsForWorkItem(workItemId) {
    try {
      const wi = await this.makeApiCall(`/wit/workitems/${workItemId}?api-version=7.0&$expand=relations`);
      const relations = wi.relations || [];
      const prLinks = relations.filter(rel =>
        String(rel.rel || '').toLowerCase().includes('artifactlink') &&
        String(rel.url || '').includes('vstfs:///Git/PullRequestId/')
      );

      const refs = prLinks
        .map(rel => this.parsePullRequestArtifactUrl(rel.url))
        .filter(Boolean);

      const unique = new Map();
      refs.forEach(ref => {
        unique.set(`${ref.repoId}:${ref.prId}`, ref);
      });

      const results = [];
      for (const ref of unique.values()) {
        const pr = await this.getPullRequestById(ref.repoId, ref.prId);
        if (pr) {
          results.push({
            pullRequestId: pr.pullRequestId,
            repositoryId: pr.repository?.id || ref.repoId,
            repositoryName: pr.repository?.name || '',
            title: pr.title || '',
            url: pr.url || ''
          });
        }
      }

      return results;
    } catch (error) {
      console.warn(`Erreur relations PR work item ${workItemId}:`, error);
      return [];
    }
  }

  async getLastDoneDemandeClientIds(limit = 30, areaPath) {
    const areaFilter = areaPath ? `AND [System.AreaPath] UNDER '${areaPath}'` : '';
    const wiql = `SELECT [System.Id]
FROM WorkItems
WHERE [System.TeamProject] = 'Fabrique'
  AND [System.WorkItemType] = 'Demande Client'
  AND [System.State] = '6- Done'
  ${areaFilter}
ORDER BY [Microsoft.VSTS.Common.ClosedDate] DESC`;

    try {
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      return ids.slice(0, Math.max(0, limit));
    } catch (error) {
      console.error('Erreur recuperation demandes client Done:', error);
      return [];
    }
  }

  // Calculer le temps moyen entre "2- Dev" et "6- Done" pour les PBI
  async getAveragePBIDevToDoneTime(startDate, endDate, sampleSize = 50) {
    const start = startDate;
    const end = endDate;

    // Récupérer les PBI complétées dans la période
    const wiql = `SELECT [System.Id], [System.Title], [System.WorkItemType]
FROM WorkItems
WHERE [System.TeamProject] = 'Fabrique'
  AND [System.WorkItemType] = 'Product Backlog Item'
  AND [System.AreaPath] UNDER 'Fabrique\\Tribus\\Tribu Argonos'
  AND [Microsoft.VSTS.Common.ClosedDate] >= '${start}'
  AND [Microsoft.VSTS.Common.ClosedDate] <= '${end}'
ORDER BY [Microsoft.VSTS.Common.ClosedDate] DESC`;

    try {
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      let ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      console.log(`PBI trouvées pour le temps Dev->Done: ${ids.length}`);
      
      if (ids.length > sampleSize) {
        ids = ids.slice(0, sampleSize);
        console.log(`Limitation à ${sampleSize} PBI (max)`);
      }
      
      if (ids.length === 0) return { averageTime: 0, pbiCount: 0 };

      // Pour chaque PBI, récupérer l'historique
      const pbiTimings = [];

      for (const id of ids) {
        try {
          // Récupérer les revisions du work item
          const endpoint = `/wit/workitems/${id}/revisions?api-version=7.0`;
          const revisionsResponse = await this.makeApiCall(endpoint);
          const revisions = revisionsResponse.value || [];

          // Chercher les dates de passage à "2- Dev" et "6- Done"
          let devDate = null;
          let doneDate = null;

          for (const revision of revisions) {
            const state = revision.fields['System.State'];
            const changedDate = revision.fields['System.ChangedDate'];

            if (state === '2- Dev' && !devDate) {
              devDate = new Date(changedDate);
            }
            
            if (state === '6- Done') {
              doneDate = new Date(changedDate);
            }
          }

          // Si on a les deux dates, calculer la différence
          if (devDate && doneDate) {
            const timeDiffMs = doneDate - devDate;
            const timeDiffDays = timeDiffMs / (1000 * 60 * 60 * 24);
            
            pbiTimings.push(Math.round(timeDiffDays * 100) / 100);
          }
        } catch (err) {
          console.error(`Erreur récupération revisions pour PBI ${id}:`, err);
        }
      }

      // Calculer la moyenne
      const averageTime = pbiTimings.length > 0
        ? pbiTimings.reduce((sum, days) => sum + days, 0) / pbiTimings.length
        : 0;

      console.log(`Temps moyen Dev->Done: ${averageTime} jours (basé sur ${pbiTimings.length} PBI)`);

      return {
        averageTime: Math.round(averageTime * 100) / 100,
        pbiCount: pbiTimings.length,
        timings: pbiTimings,
      };
    } catch (error) {
      console.error('Erreur calcul temps moyen Dev->Done:', error);
      return { averageTime: 0, pbiCount: 0, timings: [] };
    }
  }

  // Récupérer le nombre de bugs créés pendant une période donnée
  async getCreatedBugsCount(startDate, endDate) {
    const start = startDate; // '2025-08-01'
    const end = endDate;     // '2025-12-31'

    // Requête WIQL pour récupérer les bugs créés dans la période
    const wiql = `SELECT [System.Id], [System.Title], [System.State], [System.CreatedDate]
FROM WorkItems
WHERE [System.TeamProject] = 'Fabrique'
  AND [System.WorkItemType] = 'Bug'
  AND [System.AreaPath] UNDER 'Fabrique\\Tribus\\Tribu Argonos'
  AND [System.CreatedDate] >= '${start}'
  AND [System.CreatedDate] <= '${end}'
ORDER BY [System.CreatedDate] DESC`;

    try {
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      const bugs = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      const bugCount = bugs.length;

      console.log(`Bugs créés pendant la période: ${bugCount}`);

      return {
        bugCount,
        startDate,
        endDate
      };
    } catch (error) {
      console.error('Erreur récupération bugs créés:', error);
      return { bugCount: 0, startDate, endDate };
    }
  }

  // Récupérer le nombre de bugs résolus (Done) pendant une période donnée
  async getResolvedBugsCount(startDate, endDate) {
    const start = startDate; // '2025-08-01'
    const end = endDate;     // '2025-12-31'

    // Requête WIQL pour récupérer les bugs résolus dans la période
    const wiql = `SELECT [System.Id], [System.Title], [System.State], [Microsoft.VSTS.Common.ClosedDate]
FROM WorkItems
WHERE [System.TeamProject] = 'Fabrique'
  AND [System.WorkItemType] = 'Bug'
  AND [System.AreaPath] UNDER 'Fabrique\\Tribus\\Tribu Argonos'
  AND [Microsoft.VSTS.Common.ClosedDate] >= '${start}'
  AND [Microsoft.VSTS.Common.ClosedDate] <= '${end}'
ORDER BY [Microsoft.VSTS.Common.ClosedDate] DESC`;

    try {
      console.log('Query bugs résolus:', wiql);
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      console.log('Réponse bugs résolus:', wiqlResponse);

      const bugs = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      const bugCount = bugs.length;

      console.log(`Bugs résolus pendant la période: ${bugCount}`);

      return {
        bugCount,
        startDate,
        endDate
      };
    } catch (error) {
      console.error('Erreur récupération bugs résolus:', error);
      return { bugCount: 0, startDate, endDate };
    }
  }

  // Calculer le temps moyen entre "2- Dev" et "6- Done" pour les bugs
  async getAverageBugDevToDoneTime(startDate, endDate, sampleSize = 50) {
    const start = startDate;
    const end = endDate;

    // Récupérer les bugs complétés dans la période
    const wiql = `SELECT [System.Id], [System.Title], [System.WorkItemType]
FROM WorkItems
WHERE [System.TeamProject] = 'Fabrique'
  AND [System.WorkItemType] = 'Bug'
  AND [System.AreaPath] UNDER 'Fabrique\\Tribus\\Tribu Argonos'
  AND [Microsoft.VSTS.Common.ClosedDate] >= '${start}'
  AND [Microsoft.VSTS.Common.ClosedDate] <= '${end}'
ORDER BY [Microsoft.VSTS.Common.ClosedDate] DESC`;

    try {
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      let ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      console.log(`Bugs trouvés pour le temps Dev->Done: ${ids.length}`);
      
      if (ids.length > sampleSize) {
        ids = ids.slice(0, sampleSize);
        console.log(`Limitation à ${sampleSize} bugs (max)`);
      }
      
      if (ids.length === 0) return { averageTime: 0, bugCount: 0 };

      // Pour chaque bug, récupérer l'historique
      const bugTimings = [];

      for (const id of ids) {
        try {
          // Récupérer les revisions du work item
          const endpoint = `/wit/workitems/${id}/revisions?api-version=7.0`;
          const revisionsResponse = await this.makeApiCall(endpoint);
          const revisions = revisionsResponse.value || [];

          // Chercher les dates de passage à "2- Dev" et "6- Done"
          let devDate = null;
          let doneDate = null;

          for (const revision of revisions) {
            const state = revision.fields['System.State'];
            const changedDate = revision.fields['System.ChangedDate'];

            if (state === '2- Dev' && !devDate) {
              devDate = new Date(changedDate);
            }
            
            if (state === '6- Done') {
              doneDate = new Date(changedDate);
            }
          }

          // Si on a les deux dates, calculer la différence
          if (devDate && doneDate) {
            const timeDiffMs = doneDate - devDate;
            const timeDiffDays = timeDiffMs / (1000 * 60 * 60 * 24);
            
            bugTimings.push(Math.round(timeDiffDays * 100) / 100);
          }
        } catch (err) {
          console.error(`Erreur récupération revisions pour bug ${id}:`, err);
        }
      }

      // Calculer la moyenne
      const averageTime = bugTimings.length > 0
        ? bugTimings.reduce((sum, days) => sum + days, 0) / bugTimings.length
        : 0;

      console.log(`Temps moyen Dev->Done pour bugs: ${averageTime} jours (basé sur ${bugTimings.length} bugs)`);

      return {
        averageTime: Math.round(averageTime * 100) / 100,
        bugCount: bugTimings.length,
        timings: bugTimings,
      };
    } catch (error) {
      console.error('Erreur calcul temps moyen Dev->Done bugs:', error);
      return { averageTime: 0, bugCount: 0, timings: [] };
    }
  }

  // Calculer le temps moyen de traitement (création → clôture) pour les Demandes Client
  async getAverageDemandeClientTreatmentTime(startDate, endDate, sampleSize = 50) {
    const wiql = `SELECT [System.Id] FROM WorkItems
WHERE [System.TeamProject] = 'Fabrique'
  AND [System.WorkItemType] = 'Demande Client'
  AND [Microsoft.VSTS.Common.ClosedDate] >= '${startDate}'
  AND [Microsoft.VSTS.Common.ClosedDate] <= '${endDate}'
ORDER BY [Microsoft.VSTS.Common.ClosedDate] DESC`;

    try {
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      let ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      console.log(`Demandes Client trouvées pour le temps de traitement: ${ids.length}`);
      if (ids.length > sampleSize) {
        ids = ids.slice(0, sampleSize);
        console.log(`Limitation à ${sampleSize} demandes (max)`);
      }
      if (ids.length === 0) return { averageTime: 0, count: 0, timings: [] };

      const chunkSize = 200;
      const allItems = [];
      for (let i = 0; i < ids.length; i += chunkSize) {
        const chunk = ids.slice(i, i + chunkSize);
        const endpoint = `/wit/workitems?ids=${chunk.join(',')}&fields=System.CreatedDate,Microsoft.VSTS.Common.ClosedDate&api-version=7.0`;
        const res = await this.makeApiCall(endpoint);
        allItems.push(...(res.value || []));
      }

      const timings = [];
      for (const item of allItems) {
        const created = item.fields['System.CreatedDate'];
        const closed = item.fields['Microsoft.VSTS.Common.ClosedDate'];
        if (!created || !closed) continue;
        const days = (new Date(closed) - new Date(created)) / (1000 * 60 * 60 * 24);
        if (days >= 0) timings.push(Math.round(days * 100) / 100);
      }

      const averageTime = timings.length
        ? Math.round((timings.reduce((s, d) => s + d, 0) / timings.length) * 100) / 100
        : 0;

      console.log(`Temps moyen de traitement Demandes Client: ${averageTime} jours (${timings.length} tickets)`);
      return { averageTime, count: timings.length, timings };
    } catch (error) {
      console.error('Erreur calcul temps moyen Demandes Client:', error);
      return { averageTime: 0, count: 0, timings: [] };
    }
  }

  // Calculer le temps moyen entre "2- Dev" et "6- Done" pour les Demandes Client (même approche que PBI)
  async getAverageDemandeClientDevToDoneTime(startDate, endDate, sampleSize = 50) {
    const wiql = `SELECT [System.Id] FROM WorkItems
WHERE [System.TeamProject] = 'Fabrique'
  AND [System.WorkItemType] = 'Demande Client'
  AND [Microsoft.VSTS.Common.ClosedDate] >= '${startDate}'
  AND [Microsoft.VSTS.Common.ClosedDate] <= '${endDate}'
ORDER BY [Microsoft.VSTS.Common.ClosedDate] DESC`;

    try {
      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      let ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
      console.log(`Demandes Client trouvées pour Dev->Done: ${ids.length}`);
      if (ids.length > sampleSize) {
        ids = ids.slice(0, sampleSize);
        console.log(`Limitation à ${sampleSize} demandes (max)`);
      }
      if (ids.length === 0) return { averageTime: 0, count: 0, timings: [] };

      const timings = [];
      for (const id of ids) {
        try {
          const endpoint = `/wit/workitems/${id}/revisions?api-version=7.0`;
          const revisionsResponse = await this.makeApiCall(endpoint);
          const revisions = revisionsResponse.value || [];

          let devDate = null;
          let doneDate = null;
          for (const revision of revisions) {
            const state = revision.fields['System.State'];
            const changedDate = revision.fields['System.ChangedDate'];
            if (state === '2- Dev' && !devDate) devDate = new Date(changedDate);
            if (state === '6- Done') doneDate = new Date(changedDate);
          }

          if (devDate && doneDate) {
            const days = (doneDate - devDate) / (1000 * 60 * 60 * 24);
            timings.push(Math.round(days * 100) / 100);
          }
        } catch (err) {
          console.error(`Erreur révisions Demande Client ${id}:`, err);
        }
      }

      const averageTime = timings.length
        ? Math.round((timings.reduce((s, d) => s + d, 0) / timings.length) * 100) / 100
        : 0;

      console.log(`Temps moyen Dev->Done Demandes Client: ${averageTime} jours (${timings.length} tickets)`);
      return { averageTime, count: timings.length, timings };
    } catch (error) {
      console.error('Erreur calcul Dev->Done Demandes Client:', error);
      return { averageTime: 0, count: 0, timings: [] };
    }
  }

  // Temps passé dans l'état "3- Test" par les PBI et Bugs de la Tribu Argonos.
  // Tickets éligibles : passés par "3- Test" et arrivés à "4- Doc" ou au-delà
  // pendant la période (date du dernier changement d'état), du plus récent au
  // plus ancien.
  async getTestEligibleTickets(startDate, endDate) {
    const wiql = `SELECT [System.Id] FROM WorkItems
WHERE [System.TeamProject] = 'Fabrique'
  AND [System.WorkItemType] IN ('Product Backlog Item', 'Bug')
  AND [System.AreaPath] UNDER 'Fabrique\\Tribus\\Tribu Argonos'
  AND [System.State] IN ('4- Doc', '5- Validation', '6- Done')
  AND EVER [System.State] = '3- Test'
  AND [Microsoft.VSTS.Common.StateChangeDate] >= '${startDate}'
  AND [Microsoft.VSTS.Common.StateChangeDate] <= '${endDate}'
ORDER BY [Microsoft.VSTS.Common.StateChangeDate] DESC`;

    const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
      method: 'POST',
      body: JSON.stringify({ query: wiql })
    });
    const ids = (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);

    // Date du changement d'état, pour pouvoir répartir les tickets (par sprint…)
    const dates = {};
    for (let i = 0; i < ids.length; i += 200) {
      const chunk = ids.slice(i, i + 200);
      const endpoint = `/wit/workitems?ids=${chunk.join(',')}&fields=Microsoft.VSTS.Common.StateChangeDate&api-version=7.0`;
      const res = await this.makeApiCall(endpoint);
      for (const item of res.value || []) {
        dates[item.id] = new Date(item.fields['Microsoft.VSTS.Common.StateChangeDate']);
      }
    }
    return ids.map(id => ({ id, stateChangeDate: dates[id] || null }));
  }

  // Échantillon : les `sampleSize` tickets éligibles les plus récents de la période.
  async getTestStateDurations(startDate, endDate, sampleSize = 50, onProgress = () => {}) {
    const eligible = await this.getTestEligibleTickets(startDate, endDate);
    const ids = eligible.slice(0, sampleSize).map(t => t.id);
    if (ids.length === 0) return { tickets: [], candidates: 0, failed: 0 };

    const { tickets, failed } = await this.computeTestStateDurations(ids, onProgress);
    return { tickets, candidates: eligible.length, failed };
  }

  // Durée d'un ticket = somme de ses passages en "3- Test" (un retour en Dev puis
  // un nouveau passage s'additionnent). Jours calendaires. Les tickets sans
  // passage terminé en test sont ignorés.
  async computeTestStateDurations(ids, onProgress = () => {}) {
    const TEST_STATE = '3- Test';
    const SQUADS_PREFIX = 'Fabrique\\Tribus\\Tribu Argonos\\Squads\\';
    const DAY_MS = 1000 * 60 * 60 * 24;

    const tickets = [];
    let failed = 0;

    // Historique des révisions, 10 tickets à la fois
    const batchSize = 10;
    for (let i = 0; i < ids.length; i += batchSize) {
      const batch = ids.slice(i, i + batchSize);
      onProgress(Math.min(i + batchSize, ids.length), ids.length);

      const statuses = await Promise.all(batch.map(async (id) => {
        try {
          const res = await this.makeApiCall(`/wit/workitems/${id}/revisions?api-version=7.0`);
          const revisions = res.value || [];
          if (!revisions.length) return;

          let previousState = null;
          let enteredAt = null;
          let totalMs = 0;
          let passages = 0;
          for (const revision of revisions) {
            const state = revision.fields['System.State'];
            const changedAt = new Date(revision.fields['System.ChangedDate']);
            if (state !== previousState) {
              if (previousState === TEST_STATE && enteredAt) {
                totalMs += changedAt - enteredAt;
                passages++;
                enteredAt = null;
              }
              if (state === TEST_STATE) enteredAt = changedAt;
              previousState = state;
            }
          }

          if (passages === 0) return;

          const fields = revisions[revisions.length - 1].fields;
          const areaPath = fields['System.AreaPath'] || '';
          const squad = areaPath.startsWith(SQUADS_PREFIX)
            ? areaPath.slice(SQUADS_PREFIX.length).replace(/\\Etendu$/, '')
            : 'Autres';

          tickets.push({
            id,
            title: fields['System.Title'] || '',
            type: fields['System.WorkItemType'] || '',
            state: fields['System.State'] || '',
            squad,
            passages,
            days: Math.round((totalMs / DAY_MS) * 100) / 100,
          });
          return 'ok';
        } catch (err) {
          console.error(`Erreur révisions ticket ${id} (temps en Test):`, err);
          return 'failed';
        }
      }));
      failed += statuses.filter(s => s === 'failed').length;
    }

    if (ids.length > 0 && failed === ids.length) {
      throw new Error(`impossible de lire l'historique des ${failed} tickets (voir la console)`);
    }

    return { tickets, failed };
  }

  // ========== NOUVELLES MÉTHODES POUR LES TEST CASES ==========

  // Récupérer directement les test cases d'une suite de tests avec pagination
  // Compter les test cases d'un plan spécifique en utilisant l'API Test
  async getTestCaseCountByPlan(planId) {
    try {
      console.log(`🔍 Comptage test cases pour plan=${planId} via Test API`);
      
      // Essayer de récupérer les détails du plan
      const endpoint = `/testplan/Plans/${planId}?api-version=7.1`;
      console.log(`   → Endpoint: ${endpoint}`);
      
      let planDetails;
      try {
        planDetails = await this.makeApiCall(endpoint);
      } catch (err) {
        if (err.message.includes('404')) {
          console.warn(`   ⚠️ Plan ${planId} introuvable (404)`);
          return 0;
        }
        throw err;
      }
      
      console.log(`   📋 Plan details:`, JSON.stringify(planDetails).substring(0, 300));
      
      // Si le plan n'a pas de rootSuite, c'est un problème
      if (!planDetails.rootSuite || !planDetails.rootSuite.id) {
        console.warn(`   ⚠️ Pas de rootSuite.id trouvé pour plan=${planId}`);
        return 0;
      }

      // Récupérer les test cases de la root suite
      const rootSuiteId = planDetails.rootSuite.id;
      console.log(`   🔗 Root Suite ID: ${rootSuiteId}`);
      
      const testCasesEndpoint = `/testplan/Plans/${planId}/Suites/${rootSuiteId}/TestCase?api-version=7.1`;
      const testCasesResponse = await this.makeApiCall(testCasesEndpoint);
      
      const count = (testCasesResponse.value || []).length;
      console.log(`   ✓ ${count} test cases trouvés pour rootSuite=${rootSuiteId}`);
      
      return count;
    } catch (error) {
      console.error(`❌ Erreur comptage test cases pour plan=${planId}:`, error.message);
      return 0;
    }
  }

  // Récupérer récursivement TOUTES les suites d'un plan (arborescence complète)
  async getAllSuitesRecursive(planId, parentSuiteId = null, visited = new Set(), depth = 0) {
    try {
      const indent = '  '.repeat(depth);
      const key = `${planId}-${parentSuiteId || 'root'}`;
      
      if (visited.has(key)) {
        return [];
      }
      visited.add(key);

      // Si c'est le premier appel, récupérer la suite racine d'abord
      const planDetails = await this.makeApiCall(`/testplan/Plans/${planId}?api-version=7.1`);
      const rootSuiteId = planDetails.rootSuite?.id;
      
      if (!rootSuiteId) {
        console.warn(`${indent}⚠️ Pas de rootSuite trouvée pour plan=${planId}`);
        return [];
      }

      // Première suite à traiter
      let allSuites = [];
      const suiteIdsToProcess = [rootSuiteId];

      while (suiteIdsToProcess.length > 0) {
        const suiteId = suiteIdsToProcess.shift();
        const suiteKey = `${planId}-${suiteId}`;
        
        if (visited.has(suiteKey)) {
          continue;
        }
        visited.add(suiteKey);

        console.log(`${indent}📦 Traitement suite ${suiteId}`);
        allSuites.push(suiteId);

        try {
          // Récupérer les détails de la suite pour voir les enfants (avec expand=Children!)
          const suiteDetailsEndpoint = `/testplan/Plans/${planId}/Suites/${suiteId}?expand=Children&api-version=7.1`;
          const suiteDetails = await this.makeApiCall(suiteDetailsEndpoint);
          
          const childSuites = suiteDetails.children || [];
          if (childSuites.length > 0) {
            console.log(`${indent}  → ${childSuites.length} enfant(s) trouvé(s)`);
            for (const childSuite of childSuites) {
              suiteIdsToProcess.push(childSuite.id);
            }
          } else {
            console.log(`${indent}  → Pas d'enfants`);
          }
        } catch (err) {
          console.log(`${indent}  ⚠️ Erreur récupération suite ${suiteId}: ${err.message}`);
        }
      }

      return allSuites;
    } catch (error) {
      console.error(`❌ Erreur getAllSuitesRecursive planId=${planId}:`, error.message);
      return [];
    }
  }

  // Récupérer récursivement tous les test cases d'une suite et ses sous-suites
  async getAllTestCasesFromSuite(planId, suiteId, visited = new Set(), depth = 0) {
    try {
      const indent = '  '.repeat(depth);
      const key = `${planId}-${suiteId}`;
      
      // Éviter les boucles infinies
      if (visited.has(key)) {
        console.log(`${indent}⏭️  Suite ${suiteId} déjà visitée, skip`);
        return [];
      }
      visited.add(key);
      
      console.log(`${indent}🔍 Récupération test cases de suite ${suiteId}...`);
      
      // Récupérer les test cases de cette suite
      const testCaseEndpoint = `/testplan/Plans/${planId}/Suites/${suiteId}/TestCase?api-version=7.1`;
      console.log(`${indent}  📌 Endpoint: ${testCaseEndpoint}`);
      
      let testCases = [];
      try {
        const response = await this.makeApiCall(testCaseEndpoint);
        testCases = response.value || [];
        console.log(`${indent}  ✓ ${testCases.length} test cases trouvés`);
      } catch (err) {
        console.log(`${indent}  ⚠️ Erreur récupération test cases: ${err.message}`);
        // Continuer même en cas d'erreur
      }
      
      let allTestCases = [...testCases];
      
      // Récupérer les détails de la suite pour voir les sous-suites (children)
      try {
        const suiteDetailsEndpoint = `/testplan/Plans/${planId}/Suites/${suiteId}?expand=Children&api-version=7.1`;
        const suiteDetails = await this.makeApiCall(suiteDetailsEndpoint);
        
        const childSuites = suiteDetails.children || [];
        if (childSuites.length > 0) {
          console.log(`${indent}  → ${childSuites.length} sous-suite(s) trouvée(s)`);
          
          for (const childSuite of childSuites) {
            const childTestCases = await this.getAllTestCasesFromSuite(planId, childSuite.id, visited, depth + 1);
            allTestCases = allTestCases.concat(childTestCases);
          }
        }
      } catch (err) {
        console.log(`${indent}  ⚠️ Erreur récupération suites enfants: ${err.message}`);
      }
      
      return allTestCases;
    } catch (error) {
      console.error(`❌ Erreur getAllTestCasesFromSuite planId=${planId}, suiteId=${suiteId}:`, error.message);
      return [];
    }
  }

  // Compter les test cases d'un plan avec récursion (toutes les suites)
  async getTestCaseCountByPlanRecursive(planId) {
    try {
      console.log(`🔍 Comptage test cases (avec récursion) pour plan=${planId}`);
      
      // Récupérer les détails du plan pour trouver la root suite
      let planDetails;
      try {
        planDetails = await this.makeApiCall(`/testplan/Plans/${planId}?api-version=7.1`);
      } catch (err) {
        if (err.message.includes('404')) {
          console.warn(`   ⚠️ Plan ${planId} introuvable (404) - retour 0`);
          return 0;
        }
        throw err;
      }
      
      if (!planDetails.rootSuite || !planDetails.rootSuite.id) {
        console.warn(`   ⚠️ Pas de rootSuite.id trouvé pour plan=${planId}`);
        return 0;
      }

      const rootSuiteId = planDetails.rootSuite.id;
      console.log(`   🔗 Root Suite ID: ${rootSuiteId}`);
      
      // 1. Récupérer TOUTES les suites du plan (arborescence complète)
      console.log(`   📋 Récupération de toutes les suites du plan...`);
      const allSuites = await this.getAllSuitesRecursive(planId);
      console.log(`   ✓ ${allSuites.length} suite(s) trouvée(s): [${allSuites.join(', ')}]`);
      
      // 2. Pour chaque suite, récupérer les test cases
      let totalTestCases = 0;
      for (const suiteId of allSuites) {
        try {
          console.log(`   → Récupération test cases pour suite ${suiteId}...`);
          const testCaseEndpoint = `/testplan/Plans/${planId}/Suites/${suiteId}/TestCase?api-version=7.1`;
          const response = await this.makeApiCall(testCaseEndpoint);
          const count = (response.value || []).length;
          console.log(`     ✓ ${count} test cases trouvés`);
          totalTestCases += count;
        } catch (err) {
          console.log(`     ⚠️ Erreur récupération suite ${suiteId}: ${err.message}`);
        }
      }
      
      console.log(`   ✨ Total: ${totalTestCases} test cases trouvés`);
      return totalTestCases;
    } catch (error) {
      console.error(`❌ Erreur comptage plan=${planId}:`, error.message);
      return 0;
    }
  }

  // Compter les test cases d'un plan avec filtres (date, tag)
  async getTestCaseCountByPlanFiltered(planId, tagFilter = null, startDate = null, endDate = null) {
    try {
      console.log(`🔍 Comptage test cases pour plan=${planId} avec filtres`);
      
      // 1. Récupérer les détails du plan
      let planDetails;
      try {
        planDetails = await this.makeApiCall(`/testplan/Plans/${planId}?api-version=7.1`);
      } catch (err) {
        if (err.message.includes('404')) {
          console.warn(`   ⚠️ Plan ${planId} introuvable (404) - retour 0`);
          return 0;
        }
        throw err;
      }
      
      if (!planDetails.rootSuite || !planDetails.rootSuite.id) {
        console.warn(`   ⚠️ Pas de rootSuite.id trouvé`);
        return 0;
      }

      // 2. Récupérer TOUTES les suites du plan
      console.log(`   📋 Récupération de toutes les suites du plan...`);
      const allSuites = await this.getAllSuitesRecursive(planId);
      console.log(`   ✓ ${allSuites.length} suite(s) trouvée(s)`);
      
      // 3. Pour chaque suite, récupérer les test cases
      let allTestCaseIds = [];
      for (const suiteId of allSuites) {
        try {
          console.log(`   → Récupération test cases pour suite ${suiteId}...`);
          const testCaseEndpoint = `/testplan/Plans/${planId}/Suites/${suiteId}/TestCase?api-version=7.1`;
          const response = await this.makeApiCall(testCaseEndpoint);
          const testCases = response.value || [];
          
          // Extraire les IDs
          for (const tc of testCases) {
            if (tc.workItem?.id) allTestCaseIds.push(tc.workItem.id);
          }
          console.log(`     ✓ ${testCases.length} test cases trouvés`);
        } catch (err) {
          console.log(`     ⚠️ Erreur suite ${suiteId}: ${err.message}`);
        }
      }

      console.log(`   📦 ${allTestCaseIds.length} test cases totaux collectés`);
      
      if (allTestCaseIds.length === 0) {
        return 0;
      }

      // 4. Récupérer les détails des test cases (dates, tags) pour le filtrage
      const fields = ['System.Id', 'System.Title', 'System.CreatedDate', 'System.Tags'];
      const chunkSize = 200;
      const allDetails = [];

      for (let i = 0; i < allTestCaseIds.length; i += chunkSize) {
        const chunk = allTestCaseIds.slice(i, i + chunkSize);
        const idsStr = chunk.join(',');
        
        try {
          const endpoint = `/wit/workitems?ids=${idsStr}&fields=${fields.join(',')}&api-version=7.0`;
          const res = await this.makeApiCall(endpoint);
          if (res && res.value) {
            allDetails.push(...res.value);
          }
        } catch (err) {
          console.warn(`   ⚠️ Erreur chunk ${i}-${i+chunkSize}:`, err.message);
        }
      }

      console.log(`   📋 ${allDetails.length} détails récupérés`);

      // 3. Filtrer par date et tag
      let filtered = allDetails;

      if (startDate || endDate) {
        const startTime = startDate ? new Date(startDate).getTime() : 0;
        const endTime = endDate ? new Date(endDate).getTime() : Date.now();

        filtered = filtered.filter(tc => {
          const createdDate = new Date(tc.fields['System.CreatedDate']).getTime();
          return createdDate >= startTime && createdDate <= endTime;
        });
        console.log(`   📅 Après filtrage dates: ${filtered.length}`);
      }

      if (tagFilter) {
        filtered = filtered.filter(tc => {
          const tags = tc.fields['System.Tags'] || '';
          return tags.includes(tagFilter);
        });
        console.log(`   🏷️  Après filtrage tag '${tagFilter}': ${filtered.length}`);
      }

      return filtered.length;
    } catch (error) {
      console.error(`❌ Erreur comptage filtré pour plan=${planId}:`, error.message);
      return 0;
    }
  }

  // Récupérer les test cases d'une suite de tests
  async getTestCasesFromTestPlan(planId, suiteId) {
    try {
      // Utiliser la méthode récursive pour trouver tous les test cases
      const count = await this.getTestCaseCountByPlanRecursive(planId);
      return count;
    } catch (error) {
      console.error(`Erreur récupération test cases pour plan=${planId}:`, error);
      return 0;
    }
  }

  // Compter le nombre de test cases dans une suite
  async getTestCaseCountFromTestPlan(planId, suiteId) {
    try {
      return await this.getTestCaseCountByPlanRecursive(planId);
    } catch (error) {
      console.error(`Erreur comptage test cases:`, error);
      return 0;
    }
  }

  // 📋 Lister tous les plans de test disponibles
  async getAvailableTestPlans() {
    try {
      console.log('📋 Récupération de la liste des plans de test...');
      
      // Essayer l'endpoint correct: /testplan/Plans au lieu de /test/plans
      const endpoints = [
        '/testplan/Plans?api-version=7.1',
        '/testplan/Plans?api-version=7.0',
        '/testplan/Plans?api-version=7.1-preview.3'
      ];
      
      let response = null;

      for (const endpoint of endpoints) {
        try {
          console.log(`  🔄 Tentative avec: ${endpoint}`);
          response = await this.makeApiCall(endpoint);
          console.log(`  ✅ Succès avec: ${endpoint}`);
          break;
        } catch (err) {
          console.log(`  ❌ Échec avec ${endpoint}: ${err.message}`);
          continue;
        }
      }
      
      if (!response) {
        console.log('📋 Endpoint /testplan/Plans indisponible, utilisation de WIQL pour extraire les plans...');
        const plans = await this.extractPlansFromWorkItems();
        console.log(`✅ ${plans.length} plans extraits depuis les Work Items`);
        return plans;
      }
      
      if (response && response.value) {
        console.log(`✅ ${response.value.length} plans de test trouvés:`, 
          response.value.map(p => ({ id: p.id, name: p.name }))
        );
        return response.value;
      }
      return [];
    } catch (error) {
      console.error('❌ Erreur lors de la récupération des plans de test:', error.message);
      return [];
    }
  }

  // Extraire les plans de test depuis les Work Items (approche alternative)
  async extractPlansFromWorkItems() {
    try {
      console.log('  🔍 Extracting plans from Test Case work items...');
      
      // Récupérer tous les Test Cases avec leurs relations
      const wiql = `SELECT [System.Id], [System.Title], [Relations.Hierarchy-Reverse]
FROM workitems
WHERE [System.WorkItemType] = 'Test Case'
ORDER BY [System.CreatedDate] DESC`;
      
      const response = await this.makeApiCall(
        '/wit/wiql?api-version=7.0',
        'POST',
        { query: wiql }
      );
      
      const plans = new Map();
      
      if (response && response.workItems) {
        // Pour chaque Test Case, récupérer ses liens pour trouver le plan auquel il appartient
        for (const item of response.workItems.slice(0, 50)) { // Limiter à 50 pour éviter trop de requêtes
          try {
            const details = await this.makeApiCall(
              `/wit/workitems/${item.id}?api-version=7.0&$expand=fields`
            );
            
            // Vérifier si c'est lié à un Test Plan
            if (details.relations) {
              for (const rel of details.relations) {
                if (rel.rel === 'Microsoft.VSTS.TestManagement.TestSuiteContainment-Forward') {
                  // Extraire l'ID du suite depuis l'URL
                  const match = rel.url.match(/\/suites\/(\d+)/);
                  if (match) {
                    const suiteId = match[1];
                    plans.set(suiteId, `Test Suite ${suiteId}`);
                  }
                }
              }
            }
          } catch (err) {
            // Ignorer les erreurs individuelles
          }
        }
      }
      
      // Convertir le Map en array
      return Array.from(plans.entries()).map(([id, name]) => ({
        id: parseInt(id),
        name: name
      }));
    } catch (error) {
      console.error('  ❌ Erreur extraction plans:', error.message);
      return [];
    }
  }

  // Récupérer les test cases créés en 2025 avec un tag spécifique
  async getTestCasesCreatedInYear(year = 2025, tag = null) {
    try {
      // Construire la requête WIQL pour les test cases créés dans l'année fournie
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;
      
      let wiql = `SELECT [System.Id], [System.Title], [System.CreatedDate], [System.Tags]
FROM workitems
WHERE [System.WorkItemType] = 'Test Case'
  AND [System.CreatedDate] >= '${startDate}'
  AND [System.CreatedDate] <= '${endDate}'`;

      if (tag) {
        wiql += ` AND [System.Tags] CONTAINS '${tag}'`;
      }

      wiql += `
ORDER BY [System.CreatedDate] DESC`;

      const wiqlResponse = await this.makeApiCall('/wit/wiql?api-version=7.0', {
        method: 'POST',
        body: JSON.stringify({ query: wiql })
      });

      return (wiqlResponse.workItems || []).map(w => w.id).filter(Boolean);
    } catch (error) {
      console.error(`Erreur récupération test cases créés en ${year} avec tag ${tag}:`, error);
      return [];
    }
  }

  // Obtenir les statistiques des test cases créés en 2025
  async getTestCaseCreationStats(year = 2025) {
    try {
      // Test cases avec tag TC_by_AI
      const withAITag = await this.getTestCasesCreatedInYear(year, 'TC_by_AI');
      
      // Tous les test cases
      const allTestCases = await this.getTestCasesCreatedInYear(year, null);
      
      // Test cases sans le tag TC_by_AI
      const withoutAITag = allTestCases.length - withAITag.length;

      return {
        year,
        totalCreated: allTestCases.length,
        withAITag: withAITag.length,
        withoutAITag: withoutAITag
      };
    } catch (error) {
      console.error('Erreur calcul statistiques test cases:', error);
      return {
        year,
        totalCreated: 0,
        withAITag: 0,
        withoutAITag: 0
      };
    }
  }

  // Compter les test cases au début et à la fin d'une année
  async getTestCaseCountAtDateRange(planId, suiteId, startDate, endDate) {
    try {
      // Gérer les strings de dates aussi
      const startDateStr = typeof startDate === 'string' ? startDate : startDate.toISOString().split('T')[0];
      const endDateStr = typeof endDate === 'string' ? endDate : endDate.toISOString().split('T')[0];
      
      console.log(`📅 Comptage test cases: plan=${planId}, début=${startDateStr}, fin=${endDateStr}`);
      
      // Utiliser WIQL avec filtre de date
      const count = await this.getTestCaseCountByPlanFiltered(planId, null, startDate, endDate);
      console.log(`   ✓ ${count} test cases trouvés dans la plage de dates`);
      
      return count;
    } catch (error) {
      console.error(`Erreur comptage test cases par date:`, error);
      return 0;
    }
  }

  // Récupérer tous les plans de test disponibles
  async getAllTestPlans() {
    try {
      console.log('📋 Récupération de tous les plans de test...');
      
      const response = await this.makeApiCall('/testplan/Plans?api-version=7.1');
      
      if (!response || !response.value) {
        console.log('   ⚠️ Aucun plan trouv en réponse');
        return [];
      }

      const plans = response.value.map(plan => ({
        id: plan.id,
        name: plan.name,
        rootSuiteId: plan.rootSuite?.id || null
      }));

      console.log(`   ✓ ${plans.length} plan(s) trouvé(s)`);
      return plans;
    } catch (error) {
      console.error('❌ Erreur récupération plans de test:', error.message);
      return [];
    }
  }

  // ============================================================================
  // RÉSOLUTION BASÉE SUR L'HISTORIQUE DES ÉTATS
  // ============================================================================

  /**
   * Pour chaque work item, retourne la première date à laquelle il a atteint
   * un état >= "4- Doc" (priorité : "4- Doc" > "5- Validation" > "6- Done"/"6- Cancelled").
   * @param {number[]} ids
   * @returns {Promise<Map<number, Date>>}
   */
  async getFirstResolvedDates(ids) {
    const RESOLVED_STATES_PRIORITY = [
      ['4- doc'],
      ['5- validation'],
      ['6- done', '6- cancelled'],
    ];

    const result = new Map();
    if (!ids || ids.length === 0) return result;

    const CONCURRENCY = 10;

    const fetchUpdatesForId = async (id) => {
      try {
        const response = await this.makeApiCall(`/wit/workitems/${id}/updates?api-version=7.0`);
        const updates = response?.value || [];

        // Collect first date for each priority tier
        const tierDates = [null, null, null];

        for (const update of updates) {
          const stateChange = update.fields?.['System.State'];
          if (!stateChange?.newValue) continue;
          const newState = String(stateChange.newValue).trim().toLowerCase();
          const changedDate = update.fields?.['System.ChangedDate']?.newValue
            || update.revisedDate;
          if (!changedDate) continue;
          const date = new Date(changedDate);
          if (Number.isNaN(date.getTime())) continue;

          for (let tier = 0; tier < RESOLVED_STATES_PRIORITY.length; tier++) {
            if (RESOLVED_STATES_PRIORITY[tier].includes(newState)) {
              if (tierDates[tier] === null || date < tierDates[tier]) {
                tierDates[tier] = date;
              }
            }
          }
        }

        // Pick the highest-priority tier that has a date
        const resolved = tierDates[0] ?? tierDates[1] ?? tierDates[2];
        if (resolved) result.set(id, resolved);
      } catch (error) {
        console.warn(`getFirstResolvedDates: erreur pour work item ${id}`, error);
      }
    };

    // Run with concurrency limit
    for (let i = 0; i < ids.length; i += CONCURRENCY) {
      const chunk = ids.slice(i, i + CONCURRENCY);
      await Promise.all(chunk.map(fetchUpdatesForId));
    }

    return result;
  }

  // ============================================================================
  // CONFIGURATION ET CONSTANTS
  // ============================================================================

  /**
   * Récupère tous les tickets liés à des PRs sur des branches 26.1 (dans les repos cibles)
   * mais qui n'ont PAS le planned release tagué 26.1.0.
   * @param {string[]} repoNames - Noms des repos à interroger (ex: ['argonos', 'argonos-front', 'shared-devops'])
   * @param {string} branchFilter - Sous-chaîne devant être présente dans le nom de la branche cible (ex: '26.1')
   * @param {string} plannedRelease - Valeur attendue dans le champ planned release (ex: '26.1.0')
   * @returns {Promise<{items: Array, repos: Array, errors: string[]}>}
   */
  async getUntaggedWorkItemsFromPRsOn261Branch(
    repoNames = ['argonos', 'argonos-front', 'shared-devops'],
    branchFilter = '26.1',
    plannedRelease = '26.1.0',
    options = {}
  ) {
    const {
      prStatuses = ['active', 'completed'],
      releaseFieldNames = null,
      prScopes = null,
      defaultQueryTimeRangeType = null
    } = options || {};

    const errors = [];

    // 1. Résoudre le champ planned release
    const detectedFieldName = await this.getPlannedReleaseFieldReferenceName();
    const candidateFieldNames = Array.from(new Set(
      (releaseFieldNames && releaseFieldNames.length > 0
        ? releaseFieldNames
        : [
            detectedFieldName,
            'Custom.PlannedRelease',
            'Custom.Planned_Release',
            'Custom.Planned.Release'
          ]
      ).filter(Boolean)
    ));

    // 2. Récupérer les repos et filtrer sur les noms demandés
    let allRepos = [];
    try {
      allRepos = await this.getRepositories();
    } catch (e) {
      errors.push(`Erreur récupération repositories: ${e.message}`);
      return { items: [], repos: [], errors };
    }

    const targetRepos = allRepos.filter(repo =>
      repoNames.some(name => repo.name.toLowerCase() === name.toLowerCase())
    );

    const reposSummary = targetRepos.map(r => ({ id: r.id, name: r.name }));

    // 3. Pour chaque repo, récupérer les PRs (active + completed) ciblant une branche contenant branchFilter
    const STATUSES = prStatuses;
    const searchScopes = (Array.isArray(prScopes) && prScopes.length > 0)
      ? prScopes
      : [{ branchFilter }];

    const normalizeDate = (value) => {
      if (!value) return null;
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    };

    const getEffectiveQueryTimeRangeType = (scope, status) => {
      const requestedType = scope.queryTimeRangeType || defaultQueryTimeRangeType;
      // Azure DevOps n'accepte pas "closed" pour les PR active.
      if (status === 'active' && requestedType === 'closed') {
        return 'created';
      }
      return requestedType;
    };

    const shouldKeepByDate = (pr, scope, status) => {
      const fromDate = normalizeDate(scope.fromDate);
      const toDate = normalizeDate(scope.toDate);
      if (!fromDate && !toDate) return true;

      const rangeType = getEffectiveQueryTimeRangeType(scope, status);
      const dateCandidate = rangeType === 'created'
        ? pr.creationDate
        : (pr.closedDate || pr.creationDate);
      const prDate = normalizeDate(dateCandidate);
      if (!prDate) return false;
      if (fromDate && prDate < fromDate) return false;
      if (toDate && prDate > toDate) return false;
      return true;
    };

    // workItemId (Number) -> Array<{ prId, prTitle, repoName, targetBranch, prStatus, prUrl }>
    const allPrWorkItemIds = new Map();

    for (const repo of targetRepos) {
      for (const status of STATUSES) {
        for (const scope of searchScopes) {
          const scopedBranchFilter = scope.branchFilter || branchFilter;
          let skip = 0;
          const top = 100;
          let hasMore = true;

          while (hasMore) {
            const params = [
              `searchCriteria.status=${encodeURIComponent(status)}`,
              `$top=${top}`,
              `$skip=${skip}`,
            ];

            if (scope.targetRefName) {
              params.push(`searchCriteria.targetRefName=${encodeURIComponent(scope.targetRefName)}`);
            }
            if (scope.fromDate) {
              params.push(`searchCriteria.minTime=${encodeURIComponent(scope.fromDate)}`);
            }
            if (scope.toDate) {
              params.push(`searchCriteria.maxTime=${encodeURIComponent(scope.toDate)}`);
            }
            const effectiveQueryTimeRangeType = getEffectiveQueryTimeRangeType(scope, status);
            if (effectiveQueryTimeRangeType) {
              params.push(`searchCriteria.queryTimeRangeType=${encodeURIComponent(effectiveQueryTimeRangeType)}`);
            }

            const endpoint = `/git/repositories/${repo.id}/pullrequests?${params.join('&')}&api-version=7.1-preview.1`;
            try {
              const response = await this.makeApiCall(endpoint);
              const prs = response.value || [];

              for (const pr of prs) {
                const targetRef = pr.targetRefName || '';
                // Conserver seulement les PRs dont la branche cible contient le filtre attendu
                if (scopedBranchFilter && !targetRef.includes(scopedBranchFilter)) continue;
                // Double garde-fou local pour les fenêtres temporelles
                if (!shouldKeepByDate(pr, scope, status)) continue;

                // Exclure les PRs en draft
                if (pr.isDraft === true) continue;

                // Récupérer les work items liés à cette PR
                try {
                  const wiResponse = await this.makeApiCall(
                    `/git/repositories/${repo.id}/pullRequests/${pr.pullRequestId}/workitems?api-version=7.1`
                  );
                  const wiRefs = wiResponse.value || [];
                  for (const wiRef of wiRefs) {
                    const rawId = wiRef.id;
                    if (rawId == null) continue;
                    // Normaliser en Number pour garantir la cohérence avec l'API batch
                    const id = Number(rawId);
                    if (!Number.isFinite(id)) continue;

                    const prInfo = {
                      prId: pr.pullRequestId,
                      prTitle: pr.title || '',
                      repoName: repo.name,
                      targetBranch: targetRef.replace('refs/heads/', ''),
                      prStatus: pr.status,
                      prUrl: `https://dev.azure.com/${this.organization}/${this.project}/_git/${repo.name}/pullrequest/${pr.pullRequestId}`
                    };

                    if (allPrWorkItemIds.has(id)) {
                      // Ajouter seulement si ce repo/PR n'est pas déjà présent
                      const existing = allPrWorkItemIds.get(id);
                      const alreadyPresent = existing.some(
                        p => p.prId === pr.pullRequestId && p.repoName === repo.name
                      );
                      if (!alreadyPresent) existing.push(prInfo);
                    } else {
                      allPrWorkItemIds.set(id, [prInfo]);
                    }
                  }
                } catch (e) {
                  // Non-bloquant
                  console.warn(`Erreur récupération WI pour PR ${pr.pullRequestId} (${repo.name}):`, e.message);
                }
              }

              if (prs.length < top) {
                hasMore = false;
              } else {
                skip += top;
              }
            } catch (e) {
              errors.push(`Erreur récupération PRs repo=${repo.name} status=${status}: ${e.message}`);
              hasMore = false;
            }
          }
        }
      }
    }

    if (allPrWorkItemIds.size === 0) {
      return { items: [], repos: reposSummary, errors };
    }

    // 4. Récupérer les détails des work items en batch
    // Les clés sont déjà des Number
    const allIds = Array.from(allPrWorkItemIds.keys());
    let plannedReleaseFieldName = candidateFieldNames[0];

    const baseFields = [
      'System.Id',
      'System.Title',
      'System.State',
      'System.WorkItemType',
      'System.AreaPath',
      'System.CreatedDate',
      'System.AssignedTo',
      'Microsoft.VSTS.Common.Priority',
    ];

    let workItemDetails = [];
    for (const fieldName of candidateFieldNames) {
      try {
        const fields = [...baseFields, fieldName];
        const chunkSize = 200;
        const fetched = [];
        for (let i = 0; i < allIds.length; i += chunkSize) {
          const chunk = allIds.slice(i, i + chunkSize);
          const endpoint = `/wit/workitems?ids=${chunk.join(',')}&fields=${fields.join(',')}&api-version=7.0`;
          const res = await this.makeApiCall(endpoint);
          if (res && res.value) fetched.push(...res.value);
        }
        workItemDetails = fetched;
        plannedReleaseFieldName = fieldName;
        break;
      } catch (e) {
        console.warn(`Champ planned release inutilisable: ${fieldName}`, e.message);
      }
    }

    const normalizedPlannedRelease = String(plannedRelease || '').trim().toLowerCase();
    // Exclure les tickets dont le planned release contient "26.1.0" OU "26.1.x" (valeur littérale).
    const includesPlannedRelease = (value) => {
      const v = String(value || '').toLowerCase();
      return v.includes(normalizedPlannedRelease) || v.includes('26.1.x');
    };

    // 5. Cas particuliers:
    // - Task / Port: vérifier les tickets liés via parent OU related.
    const originIds = [];
    workItemDetails.forEach(wi => {
      const type = String(wi.fields['System.WorkItemType'] || '').trim().toLowerCase();
      if (type === 'task' || type === 'port') {
        originIds.push(wi.id);
      }
    });

    // workItemId -> [{ id: linkedWorkItemId, relationType: 'parent' | 'related' }]
    const relationTargetsByOriginId = new Map();
    const extractWorkItemIdFromUrl = (url) => {
      const match = String(url || '').match(/\/workItems\/(\d+)$/i);
      return match ? Number(match[1]) : null;
    };

    for (const originId of originIds) {
      try {
        const wi = await this.makeApiCall(`/wit/workitems/${originId}?api-version=7.0&$expand=relations`);
        const relations = wi.relations || [];
        const targets = [];

        relations.forEach(rel => {
          const relType = String(rel.rel || '').toLowerCase();
          const linkedId = extractWorkItemIdFromUrl(rel.url);
          if (!linkedId || !Number.isFinite(linkedId)) return;

          if (relType === 'system.linktypes.hierarchy-reverse') {
            targets.push({ id: linkedId, relationType: 'parent' });
          }
          if (relType === 'system.linktypes.related') {
            targets.push({ id: linkedId, relationType: 'related' });
          }
        });

        if (targets.length > 0) {
          const uniqueTargets = [];
          targets.forEach(target => {
            const exists = uniqueTargets.some(item => item.id === target.id && item.relationType === target.relationType);
            if (!exists) uniqueTargets.push(target);
          });
          relationTargetsByOriginId.set(originId, uniqueTargets);
        }
      } catch (e) {
        console.warn(`Impossible de recuperer les relations du ticket ${originId}:`, e.message);
      }
    }

    const referencedIds = Array.from(new Set(
      Array.from(relationTargetsByOriginId.values())
        .flatMap(targets => targets.map(target => target.id))
    ));
    const referencedById = new Map();

    if (referencedIds.length > 0) {
      const referencedFields = [
        'System.Id',
        'System.Title',
        'System.State',
        'System.WorkItemType',
        'System.AreaPath',
        'System.CreatedDate',
        'System.AssignedTo',
        'Microsoft.VSTS.Common.Priority',
        plannedReleaseFieldName
      ];

      const chunkSize = 200;
      for (let i = 0; i < referencedIds.length; i += chunkSize) {
        const chunk = referencedIds.slice(i, i + chunkSize);
        try {
          const endpoint = `/wit/workitems?ids=${chunk.join(',')}&fields=${referencedFields.join(',')}&api-version=7.0`;
          const response = await this.makeApiCall(endpoint);
          (response.value || []).forEach(referencedItem => {
            referencedById.set(referencedItem.id, referencedItem);
          });
        } catch (e) {
          console.warn('Erreur recuperation des tickets references pour task/port:', e.message);
        }
      }
    }

    // 6. Filtrer les tickets qui n'ont PAS le planned release 26.1.0
    // - Direct: check direct
    // - Task / Port: check parent OR related
    const untaggedOrigins = workItemDetails.filter(wi => {
      const type = String(wi.fields['System.WorkItemType'] || '').trim().toLowerCase();

      if (type !== 'task' && type !== 'port') {
        const pr = wi.fields[plannedReleaseFieldName] || '';
        return !includesPlannedRelease(pr);
      }

      const relationTargets = relationTargetsByOriginId.get(wi.id) || [];
      if (relationTargets.length === 0) {
        return true;
      }

      // Si AU MOINS un parent/related est tagué 26.1.0, on cache la ligne.
      const hasTaggedRelation = relationTargets.some(target => {
        const referencedItem = referencedById.get(target.id);
        if (!referencedItem) return false;
        const referencedPlannedRelease = referencedItem.fields?.[plannedReleaseFieldName] || '';
        return includesPlannedRelease(referencedPlannedRelease);
      });

      return !hasTaggedRelation;
    });

    const displayItemById = new Map();

    untaggedOrigins.forEach(origin => {
      const originType = String(origin.fields['System.WorkItemType'] || '').trim();
      const relationTargets = relationTargetsByOriginId.get(origin.id) || [];
      // Priorité d'affichage: parent puis related.
      const displayTarget = relationTargets.find(target => target.relationType === 'parent' && referencedById.has(target.id))
        || relationTargets.find(target => target.relationType === 'related' && referencedById.has(target.id))
        || relationTargets.find(target => referencedById.has(target.id))
        || null;
      const referencedItem = displayTarget ? referencedById.get(displayTarget.id) : null;
      const displaySource = referencedItem || origin;
      const displayId = displaySource.id;

      if (!displayItemById.has(displayId)) {
        const prInfoList = allPrWorkItemIds.get(origin.id) || [];
        const repos = [...new Set(prInfoList.map(p => p.repoName))];
        const firstPr = prInfoList[0] || {};

        displayItemById.set(displayId, {
          id: displaySource.id,
          title: displaySource.fields['System.Title'] || '',
          state: displaySource.fields['System.State'] || '',
          workItemType: displaySource.fields['System.WorkItemType'] || '',
          areaPath: displaySource.fields['System.AreaPath'] || '',
          createdDate: displaySource.fields['System.CreatedDate'] || null,
          assignedTo: displaySource.fields['System.AssignedTo']?.displayName || null,
          priority: displaySource.fields['Microsoft.VSTS.Common.Priority'] ?? null,
          plannedRelease: displaySource.fields[plannedReleaseFieldName] || '',
          releaseFieldName: plannedReleaseFieldName,
          repoNames: repos,
          repoName: repos.join(', '),
          prId: firstPr.prId,
          prTitle: firstPr.prTitle,
          targetBranch: firstPr.targetBranch,
          prStatus: firstPr.prStatus,
          prUrl: firstPr.prUrl,
          prList: [...prInfoList],
          sourceTickets: [],
          url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${displaySource.id}`
        });
      }

      const entry = displayItemById.get(displayId);
      const prInfoList = allPrWorkItemIds.get(origin.id) || [];
      prInfoList.forEach(prInfo => {
        const exists = entry.prList.some(existing => existing.prId === prInfo.prId && existing.repoName === prInfo.repoName);
        if (!exists) entry.prList.push(prInfo);
      });

      entry.repoNames = [...new Set(entry.prList.map(p => p.repoName))];
      entry.repoName = entry.repoNames.join(', ');
      const firstPr = entry.prList[0] || {};
      entry.prId = firstPr.prId;
      entry.prTitle = firstPr.prTitle;
      entry.targetBranch = firstPr.targetBranch;
      entry.prStatus = firstPr.prStatus;
      entry.prUrl = firstPr.prUrl;

      if (displaySource.id !== origin.id) {
        const relationType = displayTarget?.relationType || 'related';
        const sourceTicket = {
          id: origin.id,
          type: originType || '',
          title: origin.fields['System.Title'] || '',
          relationType,
          url: `https://dev.azure.com/${this.organization}/${this.project}/_workitems/edit/${origin.id}`
        };
        const existingSource = entry.sourceTickets.some(existing => existing.id === sourceTicket.id && existing.relationType === sourceTicket.relationType);
        if (!existingSource) {
          entry.sourceTickets.push(sourceTicket);
        }
      }
    });

    const items = Array.from(displayItemById.values());

    return { items, repos: reposSummary, errors };
  }

  mergePlannedReleaseValue(currentValue, plannedReleaseToAdd, separator = null) {
    const normalizedToAdd = String(plannedReleaseToAdd || '').trim();
    if (!normalizedToAdd) {
      return String(currentValue || '').trim();
    }

    const raw = String(currentValue || '');
    // Detect separator from existing value if not forced: prefer ';' when already used
    const detectedSep = separator || (raw.includes(';') ? ';' : ',');
    const tokens = raw
      .split(/[;,]/)
      .map(value => value.trim())
      .filter(Boolean);

    const alreadyPresent = tokens.some(
      token => token.toLowerCase() === normalizedToAdd.toLowerCase()
    );

    if (alreadyPresent) {
      return tokens.join(detectedSep);
    }

    return [...tokens, normalizedToAdd].join(detectedSep);
  }

  async addPlannedReleaseToWorkItem(
    workItemId,
    plannedReleaseToAdd = '26.1.0',
    { plannedReleaseFieldName = null, currentPlannedRelease = null } = {}
  ) {
    const id = Number(workItemId);
    if (!Number.isFinite(id)) {
      throw new Error(`Work item id invalide: ${workItemId}`);
    }

    const normalizedToAdd = String(plannedReleaseToAdd || '').trim();
    if (!normalizedToAdd) {
      throw new Error('La valeur planned release a ajouter est vide.');
    }

    const detectedFieldName = plannedReleaseFieldName || await this.getPlannedReleaseFieldReferenceName();
    const candidateFieldNames = Array.from(new Set([
      detectedFieldName,
      'Custom.PlannedRelease',
      'Custom.Planned_Release',
      'Custom.Planned.Release'
    ].filter(Boolean)));

    let lastError = null;

    for (const fieldName of candidateFieldNames) {
      try {
        let currentValue = currentPlannedRelease;

        if (currentValue === null || currentValue === undefined) {
          const wi = await this.makeApiCall(
            `/wit/workitems/${id}?fields=${encodeURIComponent(fieldName)}&api-version=7.0`
          );
          currentValue = wi?.fields?.[fieldName] || '';
        }

        const valueSeparator = String(fieldName || '').toLowerCase() === 'custom.release' ? ';' : null;
        const mergedValue = this.mergePlannedReleaseValue(currentValue, normalizedToAdd, valueSeparator);
        const alreadyPresent = String(currentValue || '')
          .split(/[;,]/)
          .map(value => value.trim().toLowerCase())
          .includes(normalizedToAdd.toLowerCase());

        if (alreadyPresent) {
          return {
            id,
            updated: false,
            skipped: true,
            reason: 'already_contains',
            plannedReleaseFieldName: fieldName,
            plannedRelease: mergedValue
          };
        }

        const patchBody = [
          {
            op: 'add',
            path: `/fields/${fieldName}`,
            value: mergedValue
          }
        ];

        const url = `${this.baseUrl}/wit/workitems/${id}?api-version=7.0`;
        const response = await fetch(url, {
          method: 'PATCH',
          headers: {
            ...this.headers,
            'Content-Type': 'application/json-patch+json'
          },
          body: JSON.stringify(patchBody)
        });

        if (!response.ok) {
          const text = await response.text().catch(() => '');
          throw new Error(
            text && text.trim()
              ? `HTTP ${response.status} - ${text}`
              : `HTTP ${response.status}`
          );
        }

        return {
          id,
          updated: true,
          skipped: false,
          plannedReleaseFieldName: fieldName,
          plannedRelease: mergedValue
        };
      } catch (error) {
        lastError = error;
      }
    }

    throw lastError || new Error(`Impossible de patcher le work item ${id}`);
  }

  async addPlannedReleaseToWorkItems(
    workItemIds = [],
    plannedReleaseToAdd = '26.1.0',
    { plannedReleaseFieldName = null, currentPlannedReleaseById = {} } = {}
  ) {
    const ids = Array.from(new Set(
      (workItemIds || [])
        .map(value => Number(value))
        .filter(id => Number.isFinite(id))
    ));

    if (ids.length === 0) {
      return {
        total: 0,
        updatedCount: 0,
        skippedCount: 0,
        failedCount: 0,
        updatedIds: [],
        skippedIds: [],
        failed: []
      };
    }

    const resolvedFieldName = plannedReleaseFieldName || await this.getPlannedReleaseFieldReferenceName();
    const results = [];

    for (const id of ids) {
      try {
        const result = await this.addPlannedReleaseToWorkItem(id, plannedReleaseToAdd, {
          plannedReleaseFieldName: resolvedFieldName,
          currentPlannedRelease: currentPlannedReleaseById[id]
        });
        results.push(result);
      } catch (error) {
        results.push({
          id,
          updated: false,
          skipped: false,
          error: error.message || 'Erreur inconnue'
        });
      }
    }

    const updated = results.filter(item => item.updated);
    const skipped = results.filter(item => item.skipped);
    const failed = results.filter(item => !item.updated && !item.skipped);

    return {
      total: ids.length,
      updatedCount: updated.length,
      skippedCount: skipped.length,
      failedCount: failed.length,
      updatedIds: updated.map(item => item.id),
      skippedIds: skipped.map(item => item.id),
      failed
    };
  }

}

// Configuration d'environnement
export const azureDevOpsConfig = {
  // À définir dans un fichier .env
  organization: process.env.REACT_APP_AZURE_ORG,
  project: process.env.REACT_APP_AZURE_PROJECT,
  pat: process.env.REACT_APP_AZURE_PAT,
  
  // Valeurs par défaut pour le développement (à ne pas committer en production)
  defaults: {
    organization: 'your-organization',
    project: 'your-project',
    // Ne jamais mettre le PAT ici en production !
  }
};

// Instance singleton du service
const azureDevOpsService = new AzureDevOpsService();

export default azureDevOpsService;